/*
 * HumanBase API base
 */
export const BASE_URI = '/api/';
export const SEARCH_HUMAN_EXACT_URI = `${BASE_URI}genes/search/exact/?organism=homo-sapiens&q=`;
export const SEARCH_HUMAN_SINGLE_URI = `${BASE_URI}genes/search/partial?organism=homo-sapiens&q=`;
export const SEARCH_HUMAN_SINGLE_V1_URI = `${BASE_URI}genes/search/partial?organism=homo-sapiens&version=1&q=`;
export const SEARCH_HUMAN_MULTI_URI = `${BASE_URI}genes/search/multi?organism=homo-sapiens&q=`;
export const SEARCH_MULTI_URI = `${BASE_URI}genes/search/multi?q=`;

/*
 * Documentation URLs
 */
export const DOC_URL = 'https://humanbase.readthedocs.io/en/latest/';

/*
 * Database slugs for specific resources
 */
export const BP_DB = 'gene-ontology-bp';
export const TISSUE_DB = 'brenda-ontology';
export const DISEASE_DB = 'disease-ontology';
export const SFARI_DB = 'sfari-gene';

/*
 * Databases for gene/term annotations
 */
export const ANNOTATION_DATABASES = [BP_DB, SFARI_DB, TISSUE_DB, DISEASE_DB];

/*
 * Databases for displaying term predictions
 */
interface TermDatabase {
  title: string;
  slug: string;
  params: {
    // eslint-disable-next-line camelcase
    score_cutoff: number;
    // eslint-disable-next-line camelcase
    max_term_size: number;
  };
}

export const TERM_DATABASES: TermDatabase[] = [
  {
    title: 'Biological process',
    slug: BP_DB,
    params: { score_cutoff: 0.05, max_term_size: 300 },
  },
  {
    title: 'Disease',
    slug: DISEASE_DB,
    params: { score_cutoff: 0.05, max_term_size: 300 },
  },
];

/*
 * Title to be displayed
 */
export const TITLE = 'HumanBase';

interface PageInfo {
  pageName: string;
  pageRoot: string;
}

/*
 * List of pages
 */
export const EXPECTO_PAGE: PageInfo = {
  pageName: 'expecto',
  pageRoot: '/expecto',
};
export const EXPECTOSC_PAGE: PageInfo = {
  pageName: 'expectosc',
  pageRoot: '/expectosc',
};
export const NETWAS_PAGE: PageInfo = {
  pageName: 'netwas',
  pageRoot: '/netwas',
};
export const MODULE_PAGE: PageInfo = {
  pageName: 'module',
  pageRoot: '/module',
};
export const NETWORK_PAGE: PageInfo = { pageName: 'gene', pageRoot: '/gene' };
export const DEEPSEA_PAGE: PageInfo = {
  pageName: 'deepsea',
  pageRoot: '/deepsea',
};
export const SEEK_PAGE: PageInfo = { pageName: 'seek', pageRoot: '/seek' };
export const SEI_PAGE: PageInfo = { pageName: 'sei', pageRoot: '/sei' };
export const BELUGA_PAGE: PageInfo = {
  pageName: 'beluga',
  pageRoot: '/beluga',
};
export const SEQWEAVER_ANALYSIS_PAGE: PageInfo = {
  pageName: 'seqweaver-deepsea',
  pageRoot: '/seqweaver-deepsea',
};
export const ISM_PAGE: PageInfo = { pageName: 'ism', pageRoot: '/ism' };

export const ASDBROWSER_PAGE: PageInfo = {
  pageName: 'asdbrowser',
  pageRoot: '/asdbrowser',
};
/*
 * Layout related constants
 */
export const SM_MAX = 991;
export const COMMUNITY_2ND_COL = (1 / 7) * 5;

interface ExampleGene {
  description: string;
  buttonText: string;
  pasteText: string;
  searchUrl?: string;
  url?: string;
}

/*
 * List of example genes to query
 */
export const EXAMPLE_GENES: ExampleGene[] = [
  {
    description: 'Add IL1B to your search query',
    buttonText: 'IL1B',
    pasteText: 'IL1B',
    searchUrl: `${SEARCH_HUMAN_EXACT_URI}il1b`,
    url: '/gene/3553',
  },
  {
    description: 'Add BRCA1 to your search query',
    buttonText: 'BRCA1',
    pasteText: 'BRCA1',
    searchUrl: `${SEARCH_HUMAN_EXACT_URI}brca1`,
    url: '/gene/672',
  },
  {
    description: 'Add PTEN to your search query',
    buttonText: 'PTEN',
    pasteText: 'PTEN',
    searchUrl: `${SEARCH_HUMAN_EXACT_URI}pten`,
    url: '/gene/5728',
  },
];

interface ExampleSearch {
  description: string;
  buttonText: string;
  pasteText: string;
  tissue?: string;
}

export const EXAMPLE_SEARCH: ExampleSearch[] = [
  {
    description: 'Search for cancer genes',
    buttonText: 'cancer',
    pasteText: 'cancer',
  },
  {
    description: 'Search for autism genes',
    buttonText: 'autism',
    pasteText: 'autism',
  },
];

export const MODULE_EXAMPLE_SEARCH: ExampleSearch[] = [
  {
    description: 'Search using 300 genes associated with autism',
    buttonText: 'autism-associated gene set',
    tissue: 'nervous-system',
    pasteText:
      'ADNP ARID1B DYRK1A ASH1L ASXL3 CHD8 ANK2 CUL3 DSCAM MYT1L ' +
      'KMT5B NAA15 KMT2A GRIN2B KATNAL2 PTEN RELN SCN2A SYNGAP1 SETD5 ' +
      'SHANK3 TBR1 TRIP12 CNTN4 BCKDK CACNA1H ANKRD11 DEAF1 DDX3X BCL11A ' +
      'CIC CNTNAP2 BAZ2B CACNA1D CACNA2D3 CTNND2 CUX1 DIP2C ERBIN MBOAT7 ' +
      'MECP2 FOXP1 NRXN1 MAGEL2 MED13 MED13L GABRB3 KDM5B KMT2C GIGYF2 ' +
      'GRIA1 ILF2 INTS6 IRF2BPL KAT2B KDM6A LEO1 NCKAP1 NLGN3 SHANK2 ' +
      'SMARCC2 PTCHD1 SCN9A SLC6A1 PHF3 RANBP17 RIMS1 SPAST SRCAP SRSF11 ' +
      'TAOK2 TBL1XR1 TCF20 USP7 WDFY3 TRIO UPF3B WAC TNRC6B UBN2 ' +
      'USP15 ANK3 CELF4 CNOT3 CTCF CTNNB1 CTTNBP2 DISC1 AVPR1A CNTN5 ' +
      'CNTN6 ASTN2 DYNC1H1 BTAF1 CC2D1A CYFIP1 EHMT1 CACNB2 ACHE ADA ' +
      'CNKSR2 ELP4 AGAP2 AHDC1 AKAP9 AUTS2 CNTNAP4 AMPD1 AMT ANXA1 ' +
      'APBB1 APH1A ASPM ATP10A CACNA1E CAPRIN1 CCT4 CDC42BPB CEP135 CEP41 ' +
      'CGNL1 CHMP1A CIB2 CLASP1 CUL7 DAPP1 DENR DIP2A DLGAP1 DNMT3A ' +
      'DOCK8 DPP10 DPYSL2 EFR3A ELAVL3 EP400 EMSY ETFB KCNQ2 MACROD2 ' +
      'FBN1 ITGB3 KCNQ3 MBD5 NLGN4X FOXP2 NRXN3 GPHN KAT6A KDM6B ' +
      'MTOR NLGN1 JARID2 NEXMIF HECW2 KCNJ10 KDM5C FAM92B GGNBP2 GIGYF1 ' +
      'GPC4 GRID1 GRIK5 HECTD4 HIVEP3 HMGN1 KIAA1586 KIRREL3 KMT2E LAMB1 ' +
      'LMX1B LZTR1 MFRP MYO5A MYO9B NAV2 NINL NR3C2 OPHN1 SBF1 ' +
      'OXTR SCN1A SCN8A SETBP1 SLC12A5 PRKN SEMA5A STXBP1 SMARCA4 PAH ' +
      'PLCB1 PRICKLE1 RBFOX1 TANC2 SETD2 PHIP PRICKLE2 PRODH ROBO2 NUAK1 ' +
      'OR52M1 OTUD7A P2RX5 P4HA2 PARD3B PAX5 PER2 PHB PHRF1 PLXNA4 ' +
      'PLXNB1 PON1 PREX1 PRKCB PTK7 PYHIN1 RAB2A RAB43 RBM27 SAE1 ' +
      'SHANK1 SHOX SLC35B1 SLC38A10 SLC6A3 SLC7A3 SLC7A5 SMAD4 SPARCL1 STXBP5 ' +
      'TAF6 TRAPPC9 TSC2 ZMYND11 TMLHE UBE3A TBC1D31 TCF7L2 WWOX ZBTB20 ' +
      'ZNF804A TERF2 TET2 TRPC6 TRPM1 UBE3C UBR5 UNC79 USP45 VIL1 ' +
      'ZC3H4 ARNT2 ATP1A3 ATRX CADPS2 CAMK2A CUX2 ADORA2A ARHGEF9 CSMD1 ' +
      'CYP11B1 DIXDC1 ARHGAP32 ASMT CADM1 CD38 EXT1 ADCY5 AFF2 CTNNA3 ' +
      'DDX53 EP300 FABP5 AGBL4 BRCA2 BRD4 CDH11 CMIP DPP6 ESR2 ' +
      'CASK CHD1 ABAT ABCA10 ABCA13 ABCA7 ACE ADK DRD3 ADORA3 ' +
      'ADRB2 AGAP1 AGMO AGO1 AGTR2 BBS4 APBA2 ARHGAP5 ARHGEF10 CNTNAP5 ',
  },
  {
    description: 'Search using renal disease panel genes',
    buttonText: 'renal disease panel',
    tissue: 'kidney',
    pasteText:
      'ABCB11 ABCB4 ABCC2 ACE ACP5 ACTG2 ACTN4 ACVR2B AGPS AGT AGTR1 ' +
      'AGXT AHI1 AIPL1 AKR1D1 ALDOB ALG8 ALG9 ALPL AMER1 ANKH ANKS6 ' +
      'ANLN ANO5 ANOS1 AP2S1 ARHGAP31 ARHGDIA ARL13B ARL3 ARL6 ARMC5 ' +
      'ARMC9 ARSL ATP6V0A4 ATP6V1B1 ATP8B1 ATR AVPR2 B9D1 B9D2 BAAT ' +
      'BBS1 BBS10 BBS12 BBS2 BBS4 BBS5 BBS7 BBS9 BCS1L BICC1 BMP1 ' +
      'BMP4 BMPR1B BNC2 BSND C2CD3 CA2 CABP4 CANT1 CASP10 CASR CC2D2A ' +
      'CCDC103 CCDC28B CCDC39 CCDC40 CCDC65 CCN6 CCNO CD2AP CDKN1C ' +
      'CENPF CENPJ CEP120 CEP152 CEP164 CEP290 CEP41 CEP55 CEP83 CFAP298 ' +
      'CFAP418 CFAP53 CFTR CHD1L CHD7 CHRNA3 CHST3 CHSY1 CILK1 CLCN5 ' +
      'CLCNKA CLCNKB CLDN16 CLDN19 COL10A1 COL4A1 COL4A3 COL4A4 COL4A5 ' +
      'COL9A3 COMP COQ2 COQ6 COQ8B COQ9 CPLANE1 CRB1 CRB2 CRELD1 ' +
      'CRTAP CRX CSPP1 CTNS CTU2 CUBN CUL3 CWC27 CYP7B1 DCDC2 DDR2 ' +
      'DDX59 DGKE DGUOK DHCR7 DICER1 DLL3 DMP1 DNAAF1 DNAAF11 DNAAF2 ' +
      'DNAAF3 DNAAF4 DNAAF5 DNAH11 DNAH5 DNAI1 DNAI2 DNAJB11 DNAL1 ' +
      'DRC1 DSTYK DUSP6 DYM DYNC2H1 DYNC2I1 DYNC2I2 DYNC2LI1 DZIP1L ' +
      'EBP EIF2AK3 EMP2 ENPP1 ESCO2 EVC EVC2 EXT1 EXT2 EYA1 FAH ' +
      'FAM20C FAN1 FAS FASLG FAT4 FEZF1 FGF17 FGF23 FGF8 FGFR1 FGFR2 ' +
      'FKBP10 FLNB FLRT3 FN1 FOXP1 FRAS1 FREM1 FREM2 FSHB FXYD2 ' +
      'GANAB GATA3 GDF1 GDF5 GFM1 GHR GLA GLI2 GLI3 GLIS2 GLIS3 ' +
      'GNA11 GNAS GNPAT GNRH1 GNRHR GPC3 GPC6 GREB1L GRIP1 GUCY2D ' +
      'HAAO HAMP HESX1 HEXA HFE HNF1B HNF4A HOXA13 HOXD13 HPSE2 ' +
      'HS6ST1 HSD11B2 HSD3B7 HSPG2 HYDIN HYLS1 IFITM5 IFT122 IFT140 ' +
      'IFT172 IFT27 IFT43 IFT80 IFT81 IHH IL17RD IMPDH1 INF2 INPP5E ' +
      'INPPL1 INVS IQCB1 ITGA3 ITGA8 JAG1 KANK2 KCNJ1 KCNJ10 KCNJ13 ' +
      'KCNJ5 KDM6A KIAA0586 KIF14 KIF22 KIF7 KISS1 KISS1R KLHL3 KMT2D ' +
      'KYNU LAGE3 LAMB2 LBR LCA5 LCAT LCT LEP LEPR LHB LHX3 LHX4 ' +
      'LIFR LMF1 LMX1B LRAT LRIG2 LRP4 LRP5 LZTFL1 MAFB MAGI2 ' +
      'MAPKBP1 MATN3 MCEE MERTK MESP2 MGP MKKS MKS1 MMAA MMAB ' +
      'MMADHC MMP13 MMP21 MMP9 MMUT MPV17 MUC1 MYH9 MYO1E MYO5B ' +
      'MYO7A MYOCD NADSYN1 NBAS NEK1 NEK8 NEUROG3 NIPBL NKX2-5 NKX3-2 ' +
      'NME8 NMNAT1 NODAL NOG NOTCH2 NPC1 NPC2 NPHP1 NPHP3 NPHP4 ' +
      'NPHS1 NPHS2 NPR2 NR0B1 NR0B2 NR1H4 NR3C2 NSDHL NSMF NUP107 ' +
      'NUP93 OBSL1 OCRL ODAD1 ODAD2 ODAD3 OFD1 OSGEP OTX2 P3H1 ' +
      'PAPSS2 PAX2 PBX1 PCSK1 PDE4D PDE6D PDSS2 PEX1 PEX10 PEX12 ' +
      'PEX2 PEX26 PEX5 PEX6 PEX7 PHEX PHF6 PIBF1 PKD1 PKD1L1 PKD2 ' +
      'PKHD1 PLCE1 PLOD2 PMM2 PNPLA6 POLG POLR3B POMC POU1F1 PPARG ' +
      'PPIB PRKAR1A PRKCSH PROK2 PROKR2 PROM1 PROP1 PRPH2 PTH1R PTHLH ' +
      'PTPRO RBBP8 RD3 RDH12 RDH5 REN RET RHO RLBP1 RMND1 RNF216 ' +
      'ROBO1 ROBO2 ROR2 RPE65 RPGRIP1 RPGRIP1L RRM2B RSPH1 RSPH4A ' +
      'RSPH9 RUNX2 SALL1 SALL4 SBDS SCARB2 SCNN1A SCNN1B SCNN1G ' +
      'SDCCAG8 SEC61A1 SEC63 SEMA3A SERPINA1 SERPINF1 SERPINH1 SGPL1 ' +
      'SH3PXD2B SIX1 SIX2 SIX5 SLC12A1 SLC12A3 SLC25A13 SLC25A15 ' +
      'SLC26A2 SLC26A3 SLC2A2 SLC34A1 SLC34A3 SLC35D1 SLC4A1 SLC4A4 ' +
      'SLCO1B1 SLCO1B3 SLIT2 SMARCAL1 SMPD1 SOX10 SOX11 SOX17 SOX2 ' +
      'SOX3 SOX9 SPAG1 SPATA7 SPINT2 SPRY4 STRA6 SUFU TAC3 TACR3 ' +
      'TBC1D1 TBX15 TBX18 TBX3 TBX5 TCTN1 TCTN2 TCTN3 TFR2 TJP2 ' +
      'TMEM107 TMEM138 TMEM216 TMEM231 TMEM237 TMEM67 TNFRSF11B TP53RK ' +
      'TPRKB TRAF3IP1 TRAP1 TRIM32 TRIP11 TRMU TRPC6 TRPS1 TRPV4 TSC1 ' +
      'TSC2 TTC21B TTC37 TTC8 TULP1 TXNDC15 UGT1A1 UMOD UPK3A VHL ' +
      'VIPAS39 VPS33B WDR11 WDR19 WDR35 WDR4 WDR73 WNK1 WNK4 WNT4 ' +
      'WNT5A WNT7A WT1 XPNPEP3 XYLT1 ZIC3 ZMYND10 ZNF423',
  },
  {
    description: 'Search using synapse GO term genes',
    buttonText: 'synapse gene set',
    tissue: 'brain',
    pasteText:
      'CDK5 ERC1 NCS1 CNR1 MARCKSL1 P2RY1 P2RY2 ADORA1 P2RY4 P2RX1 P2RX2 KCNH1 ' +
      'SV2B ERC2 NPY1R NPY OSBPL2 CBARP RYR2 ATP2B3 CALB1 CALB2 TSPOAP1 CACNA1A ' +
      'CACNA1B HTR1B CACNA1D CACNA1E CACNB2 CACNB4 RIMBP2 KCNJ9 CASR GRIK1 ' +
      'GRIK3 GRIK4 GRIK5 HTR3A GLRA1 GRIA1 GRIA2 GRIA3 GRIA4 GABRA1 GABRA3 ' +
      'GABRA2 GABRA5 GABRB2 GABRB1 GRIN2B GRIN2A GRIN1 GRIN2D GRIN3B KCNMA1 ' +
      'GRIK2 GABRR1 KCNA4 KCNC2 KCNC1 KCNC4 KCNA1 KCNA2 KCNQ5 KCNJ3 KCNJ6 SCN2A ' +
      'SCN10A SCN1A KCNMB4 KCNJ8 KCNJ11 GABBR1 KCTD12 KCTD8 KCTD16 HCN1 ARFGAP3 ' +
      'NECAP1 AAK1 CALM2 CALM1 CALM3 NSF STXBP5 SYN1 SYN2 SYN3 ATG7 BSN CDK5R1 ' +
      'PRKAR1B DNAJC5 NRXN1 PLD1 SNAP29 ARHGDIA RAB3B PLS3 RAPGEF4 LPAR1 PPP3R1 ' +
      'SCRN1 PSEN1 BLOC1S6 BLOC1S1 VPS18 C9ORF72 CASP3 PCLO DOCK1 CTNNB1 ABI1 ' +
      'ABI2 CTBP1 RAB35 TBC1D24 RAB11A RAB7A CTSD AP3D1 AP1S2 AP1G1 BTBD8 VAMP4 ' +
      'STX6 STX12 VTI1A STX7 SNAP25 VAMP2 UNC13C UNC13A UNC13B STX1A STX1B ' +
      'PPFIA3 STX3 MCTP2 MCTP1 OTOF VAMP7 RELN DOC2A DOC2B SYT7 SYT1 SYT2 SYT9 ' +
      'PRRT2 RIMS1 RIMS2 SNAPIN PRKCG PRKCA PRKCB CASK FBXL20 PPFIA2 SV2C ' +
      'STXBP5L HTR1D GIT1 GIT2 CSPG5 DTNBP1 WNT7A DVL1 FBXO45 TPRG1L GPR151 ' +
      'SLC4A8 CTBP2 CAMK2A SEPTIN5 RAB3A VAMP1 CPLX1 CPLX2 CPLX3 NAPA STXBP1 ' +
      'SV2A CADPS CADPS2 RPH3A EFR3A NAPB SYP SYNGR3 BRSK1 SLC18A2 SLC17A7 ' +
      'SLC17A8 SLC17A6 SLC32A1 SLC18A3 SLC17A5 SLC10A4 GAPDH TMEM163 SLC30A3 ' +
      'CLCN3 ATP6V0E2 ATP6V1A ATP6V1B2 ATP6V1C1 ATP6V1D ATP6V1E1 ATP6V1F ' +
      'ATP6V0D1 ATP6AP1 ATP6AP2 ATP6V1G2 ATP6V1G1 ATP6V0C ATP6V0A1 ATP6V1H ' +
      'ATP6V0A4 ATP6V1B1 ATP6V0B DMXL2 SLC9A6 RAB27B CANX ARF6 CLTC SH3GL2 DNM1 ' +
      'DNM3 SNAP91 PICALM AMPH PACSIN1 AP2M1 RAB5A EPS15 EPS15L1 EPN1 ITSN1 ' +
      'EHD1 DNM2 BIN1 STON2 DNM1L SNCA SNCB AP2B1 SCRIB DIAPH1 AP2S1 FCHO2 ' +
      'PIK3C3 CAPN2 AP3B2 AP3M2 SGIP1 AP2A1 AP2A2 CLTA CLTB SYNJ2 ATP8A1 ' +
      'DENND1A CACFD1 PPP3CC SCAMP5 ACTB PIP5K1C ACTG1 PPP3CB LRRK2 DGKQ ' +
      'VPS35 SYT11 OPHN1 PRKN VPS29 SNX9 KCNC3 BCL2L1 MYLK PARK7 CXADR RAC1 ' +
      'ARPC3 COPS4 TRPM7 SLC2A4 SYNJ1 HSPA8 DNAJC6 SYT10 RAB10 RAB5B RAB5C ' +
      'EHBP1 P2RX7 PTPN23 PRKCE EVI5 EVI5L TBC1D2 TBC1D2B RAB11B SLC6A5 SLC6A9 ' +
      'SLC6A11 SLC1A6 SLC1A7 SLC6A3 SLC6A1 SLC6A4 SLC6A2 SLC1A2 FLOT1 ARL6IP5 ' +
      'DRD3 DRD2 SGTA SIPA1L2 SYNPO MYO5A WNT5A FZD9 GRID2IP RYR3 STIM2 STIM1 ' +
      'ATP2B2 SLC8A1 SLC8A2 SLC8A3 ITPR1 GRM1 CHRM1 NR3C2 RGS7 RGS7BP NOS1 ANO6 ' +
      'DPP6 KCNT1 KCNIP1 KCNIP3 KCNIP2 ADRB1 CHRNA1 CHRNE CHRNB1 CHRNG CHRND ' +
      'CHRNB2 CHRNB4 CHRNA3 CHRNA4 CHRNA7 CHRNA9 CHRNA10 GLRA2 GLRB GABRB3 ' +
      'GABRA4 GABRA6 GABRG2 GABRG3 GABRD GABRE GABRR2 GRIN2C GRIN3A GLRA3 KCND2 ' +
      'KCNK1 KCND3 HOMER1 HOMER3 BEGAIN AKAP9 NPTX1 NPTX2 DLGAP2 SRC NPTXR ' +
      'SHISA9 CNIH2 SHISA7 SHISA6 CACNG5 CACNG4 NETO1 NETO2 NRXN2 CHRNA5 CACNG7 ' +
      'LRP8 NOTCH1 MAPK8IP1 GPR39 PRKCI EFNB2 FLOT2 NRXN3 EPS8 CTNND1 ERBIN ' +
      'FRRS1L PORCN CPT1C CACNG2 DRD4 DRD5 YWHAE DLG3 CACNG8 CLPTM1 CTNND2 ' +
      'ZDHHC5 STX4 AGAP3 RAP2A SST RALBP1 SNX27 SNX6 TFRC HSP90AA1 PPFIA1 ' +
      'CNTNAP2 SYT3 SHROOM4 GRID1 CAMK2G ATG5 GRID2 INPP4A NSG2 IGSF11 PDLIM4 ' +
      'KIF21B ALS2 CSNK2A1 PHLDB2 C22ORF39 NINJ1 GPHN ADAM22 LGI1 DLG2 DLG1 ' +
      'DLG4 CNIH3 CACNG3 LHFPL4 RAPSN C1QL2 C1QL3 ERBB2 ERBB4 GNAI1 GPSM2 VWC2 ' +
      'OLFM2 PRKCZ IQSEC2 MAP2K1 DAG1 MAGI2 TNIK GSK3B ADAM10 RAP1A HRAS KALRN ' +
      'PAK1 PAK2 PAK3 GHSR GPC6 GPC4 NBEA OGT CYLD EXOC4 CACNA2D2 OLFM1 EPB41L3 ' +
      'GABARAP TRAF6 TMEM108 KIF2C VPS26B SNAP23 SNAP47 SACM1L EPB41L1 SLC12A5 ' +
      'ITGB3 ITGB1 RDX MMP9 ZDHHC2 MYO6 CALY SH3GLB2 AP3M1 RAB11FIP5 TMUB1 ' +
      'HUWE1 AKAP5 IQSEC1 HAP1 NEDD4 HPCA SYNE1 GSG1L NRG1 SIRT2 ARC ATAD1 ' +
      'PICK1 USP46 NUMB RNF216 HIP1 RALA TSPAN7 VAC14 RABEP1 TAMALIN LRP1 MDM2 ' +
      'RNF220 SYT17 SUSD4 NEDD4L MIB2 CBLB USP6 RNF34 NCDN PIKFYVE SYT6 SYT4 ' +
      'ARRB2 NF1 CAMK4 CRTC1 ANKS1B RNF10 STAT3 NSMF HTT KPNA1 CABP1 KPNA2 PRR7 ' +
      'RELA JAK2 WNT3A WLS PTPRS PTPRD FGF22 DAGLA FABP5 PLCB1 ABHD6 MGLL CNR2 ' +
      'FAAH CNRIP1 ALK GUCY1A1 IL1RAPL1 IL1RAP TENM2 CADM1 FARP1 NPTN EFNB3 ' +
      'PLAT PLG BDNF NTRK2 SLITRK5 SORCS2 CBLN2 CBLN4 NEO1 CBLN1 IGSF21 ADCY8 ' +
      'PRKAR2A PRKAR2B ADCY1 BAIAP2 BAIAP3 AKAP12 PLCG1 PLCB4 PDE4A HNRNPK ' +
      'PPP1R9A TUBB2B EPHB1 FYN MYO5B PLPPR4 GRM5 CDH2 STAU1 AKAP7 P2RX3 CDH11 ' +
      'SLC6A6 PCDH8 TNR LRRC4 ABR BCR LRRC4C NTNG1 NTNG2 DGKB DGKE L1CAM LRFN2 ' +
      'NCAM1 PTPRA NCAN NRG3 GPER1 RGS14 PACSIN2 PTN ROR2 VGF MAPK3 CFL1 CPEB1 ' +
      'CDKL5 CNTNAP4 SYNGAP1 SHANK1 SHANK3 DLGAP1 DLGAP3 EPHA7 CAMKV PFN1 PFN2 ' +
      'CHMP2B ARHGAP44 UBE3A WNK1 BTBD9 KCNN2 PAFAH1B1 RPS6KB1 TRIO MAPK8 MAPK9 ' +
      'SYNPR RTN4 IGSF8 NXPH1 NXPH4 SYNGR1 GNAI2 NLGN4X TRPC1 UBE2I CYP46A1 ' +
      'LYPD6 CYTH1 APBA1 APBA2 ADORA2A ADRB2 ADRA2A ADRA1A ADORA2B ADORA3 HTR2A ' +
      'HTR7 GIPC1 BACE1 HTR1A CHRNB3 CHRNA6 CHRM2 DGKI GRM4 GRM7 GRM8 DRD1 ' +
      'YWHAH NRN1 SYT12 GRM2 NGFR OPRM1 LAMP5 ABCC8 EEA1 KCNK2 INSYN1 INSYN2A ' +
      'PPP3CA DCC NRGN GHRL SORCS3 PRKACA INA NEFL NEFH IGF1 IGF1R PTK2B TENT2 ' +
      'NGDN SLC1A1 GRM3 SNX14 GNAO1 CLMP RASGRF1 RASGRF2 BRAF PENK NPY5R NPY2R ' +
      'CCK NPPA FLNA ADGRL3 APBB1 ADGRL2 CD2AP NFASC NLGN1 CACNA2D3 IQSEC3 ' +
      'SPTBN2 SEPTIN7 NRP1 CNKSR2 ARHGAP39 STK38 STK38L DLG5 HIP1R WASF2 ACTR3 ' +
      'WASL LAMA5 ITGA3 CD44 CHMP4B NRP2 SEMA3F RHEB ARHGAP22 SHANK2 PTPRF ' +
      'TANC1 TSC2 ABHD17B ABHD17A ABHD17C NRCAM VHL ARF4 ASAP1 DACT1 PSEN2 ' +
      'TAOK2 DISC1 CDC42 AKT1 MARK2 RBMX HNRNPM TIAM1 RPS6KA5 MINK1 MYO9A AFDN ' +
      'FILIP1 RAP1B CTTN DLGAP4 SARM1 EGLN1 RHOA RHOB AGAP1 ANK3 PDXP STRN4 ' +
      'STRN PHACTR1 MRTFA MRTFB RAPGEF2 SNX17 ZNF804A WASF3 WASF1 CTNNA2 ITPKA ' +
      'SPTB DIXDC1 CAP1 ABI3 CTTNBP2 CYFIP1 MARCKS MYH10 PTK2 DIP2A PRMT8 CAP2 ' +
      'LIMK1 AMOT KIF5B ZDHHC17 RIMS3 RIMS4 NCKAP1 VCP AGRN MECP2 CSNK2B CAMK1 ' +
      'SEPTIN11 SPARC TUBA1A TUBB APOE SEMA3A SPARCL1 CDH8 GPM6A NECTIN1 PTPRO ' +
      'PTPRT SLITRK4 SLITRK1 SLITRK2 MAP1A ACTN1 CAMK2B DBNL ACTN2 ACTN4 MPP2 ' +
      'TANC2 LAMB2 CDH9 EPHA3 PCDH17 IGSF9B EFNA5 FLRT3 LRFN3 LRFN4 LRFN5 ' +
      'LRRC4B SLITRK3 CDH10 CDH6 TENM3 TENM4 ELFN2 ELFN1 CADM2 CADM3 CADM4 ' +
      'ADAM11 MAPK14 MDGA1 MDGA2 YWHAZ PLXND1 SYDE1 SYDE2 ADD2 EFNB1 SDCBP ' +
      'CNTN5 PPFIA4 RAPH1 FZD5 EPHB2 LRRTM4 PPFIBP2 NTN1 NRG2 SRCIN1 ELAVL2 ' +
      'CHD4 ICAM5 EXOC7 SDC2 SRGAP2 PODXL SRGAP2C SRGAP2B CRTAC1 RTN4R VLDLR ' +
      'SRPX2 SEMA4D LRRTM1 LRRTM3 IL1RAPL2 NTRK3 APP NLGN2 FZD1 CLSTN3 ATG9A ' +
      'PSD ARHGEF15 LZTS1 LZTS3 NUMBL PUM2 LRP4 ARHGAP12 IQGAP1 NECTIN3 FAM107A ' +
      'NEURL1 CPEB3 TRIM47 PALM ZDHHC8 ARHGAP33 NCKIPSD DPYSL2 SENP1 CRMP1 ' +
      'CC2D1A PDLIM5 SAMD4A SYNDIG1 DCLK1 DCX PPP1R9B ELMO1 RHOG DOCK10 DOCK4 ' +
      'MARK1 SIGMAR1 NEDD8 UBE2M NAE1 CARMIL3 SRGAP3 RAC3 SEMA4C FLRT2 USP9X ' +
      'PCDH7 S1PR2 MAP1B ASIC2 ASIC1 PTPN1 CYFIP2 UBE3B HTR4 GNA13 NDFIP1 NTF3 ' +
      'TOR1A UCHL1 ELMO2 TRPC6 ORAI1 CRKL NLGN3 DOCK7 CRK ARHGEF9 GAP43 ABL1 ' +
      'EEF1A1 EEF1A2 CSMD2 FAM81A LRRTM2 SIPA1L1 LRFN1 CRIPT PRICKLE2 PRICKLE1 ' +
      'CASKIN1 FGFR1 LATS1 APH1B EPHA4 NGEF ADGRB3 PLXNC1 SKP1 C1QL1 VANGL2 ' +
      'CD47 RAI14 TRIOBP BCAN ABL2 ROBO2 SLIT2 VEZT LNX1 EXOC2 CLSTN1 ROCK1 ' +
      'ROCK2 CNTNAP1 DAB1 AGO2 FGF7 SEMA7A IGSF9 GRN PLXNA4 SORT1 APPL1 TENM1 ' +
      'DGKZ CLASP2 ARHGEF7 DAAM1 TLN2 ENAH ARF1 ADGRB1 DBN1 NOS1AP FRMPD4 CPNE6 ' +
      'EZR SLC6A17 SLC6A8 SLC6A7 PGK1 RNF19A CDH1 USP8 FBXO2 DDHD2 MTOR ' +
      'CPEB2 FMR1 EIF4E EIF4A3 EIF2B2 EIF3A EIF4A1 EIF4G1 PABPC1 EIF3C EIF3E ' +
      'EIF3F EIF3H EIF3I EIF3K EIF3M EIF3B EIF3D EIF1 EIF1AX EIF1AY EIF2A ' +
      'EIF2S3 EIF2B1 EIF2B3 EIF2B4 EIF2B5 EIF2S1 EIF2S2 EIF4EBP1 ACHE COMT ' +
      'SEPTIN3 PSMA1 PSMA2 PSMA3 PSMA4 PSMA5 PSMA6 PSMA7 PSMA8 PSMB1 PSMB2 ' +
      'PSMB3 PSMB4 PSMB5 PSMB6 PSMB7 PSMB8 PSMB9 PSMB10 PSMC1 PSMC2 PSMC3 PSMC4 ' +
      'PSMC5 PSMC6 PSMD1 PSMD2 PSMD3 PSMD4 PSMD5 PSMD6 PSMD7 PSMD8 PSMD9 PSMD10 ' +
      'PSMD11 PSMD12 PSMD13 PSMD14 SEM1 ADRM1 PSME1 PSME2 PSME3 PSME4 PSMF1 ' +
      'ANAPC1 ANAPC2 CDC27 ANAPC4 ANAPC5 CDC16 ANAPC7 CDC23 ANAPC10 ANAPC11 ' +
      'CDC26 ANAPC13 ANAPC15 ANAPC16 FZR1 CDC20 RAB26 PLEKHG5 USP5 CAPN1 TRIM3 ' +
      'PIN1 KLHL17 CUL3 UBE2N UBE2V1 PLK2 GAD1 GAD2 RPL22 RPL9 RPL10 RPL10A ' +
      'RPL11 RPL12 RPL13 RPL13A RPL14 RPL17 RPL15 RPL18 RPL23 RPL23A RPL26 ' +
      'RPL24 RPL27 RPL27A RPL28 RPL29 RPL32 RPL34 RPL35 RPL35A RPL36 RPL36A ' +
      'RPL37 RPL37A RPL38 RPL4 RPL5 RPL6 RPL7 RPL7A RPL8 RPLP0 RPLP2 UBA52 ' +
      'RPS10 RPS11 RPS12 RPS14 RPS15A RPS16 RPS24 RPS26 RPS27A RPS28 RPS5 RPS13 ' +
      'RPS27 RPSA RPS2 RPS3 RPS3A RPS4X RPS4Y1 RPS4Y2 RPS6 RPS7 RPS8 RPS9 RPS15 ' +
      'RPS17 RPS18 RPS19 RPS20 RPS21 RPS23 RPS25 RPS27L RPS29 FAU RPLP1 RPL3 ' +
      'RPL3L RPL7L1 RPL10L RPL18A RPL19 RPL21 RPL22L1 RPL26L1 RPL30 RPL31 ' +
      'RPL36AL RPL39 RPL39L RPL41 EEF2 ELAVL4 EEF2K ATG16L1 TMEM230 RAB21 KIF2A ' +
      'KIF21A FEZ1 TERF2 RABGEF1 RAB17 KIF1A KIF3A KIF5A KIF17 SEPTIN9 KIF3B ' +
      'KIFAP3 PURA HNRNPU SFPQ STAU2 KIF5C MADD ARL8A ARL8B GRIPAP1 RAB4A MKLN1 ' +
      'RAB8A MAPK10 GRIP1 GRIP2 NSG1',
  },
];

// FASTA Example for 1000bp sequences (Seqweaver)
export const FASTA_EXAMPLE_TEXT_1000BP = `>known_CEBP_binding_increase-GtoT__chr1_109817091_109818090
GTGCCTCTGGGAGGAGAGGGACTCCTGGGGGGCCTGCCCCTCATACGCCATCACCAAAAGGAAAGGACAAAGCCACACGC
AGCCAGGGCTTCACACCCTTCAGGCTGCACCCGGGCAGGCCTCAGAACGGTGAGGGGCCAGGGCAAAGGGTGTGCCTCGT
CCTGCCCGCACTGCCTCTCCCAGGAACTGGAAAAGCCCTGTCCGGTGAGGGGGCAGAAGGACTCAGCGCCCCTGGACCCC
CAAATGCTGCATGAACACATTTTCAGGGGAGCCTGTGCCCCCAGGCGGGGGTCGGGCAGCCCCAGCCCCTCTCCTTTTCC
TGGACTCTGGCCGTGCGCGGCAGCCCAGGTGTTTGCTCAGTTGCTGACCCAAAAGTGCTTCATTTTTCGTGCCCGCCCCG
CGCCCCGGGCAGGCCAGTCATGTGTTAAGTTGCGCTTCTTTGCTGTGATGTGGGTGGGGGAGGAAGAGTAAACACAGTGC
TGGCTCGGCTGCCCTGAGGTTGCTCAATCAAGCACAGGTTTCAAGTCTGGGTTCTGGTGTCCACTCACCCACCCCACCCC
CCAAAATCAGACAAATGCTACTTTGTCTAACCTGCTGTGGCCTCTGAGACATGTTCTATTTTTAACCCCTTCTTGGAATT
GGCTCTCTTCTTCAAAGGACCAGGTCCTGTTCCTCTTTCTCCCCGACTCCACCCCAGCTCCCTGTGAAGAGAGAGTTAAT
ATATTTGTTTTATTTATTTGCTTTTTGTGTTGGGATGGGTTCGTGTCCAGTCCCGGGGGTCTGATATGGCCATCACAGGC
TGGGTGTTCCCAGCAGCCCTGGCTTGGGGGCTTGACGCCCTTCCCCTTGCCCCAGGCCATCATCTCCCCACCTCTCCTCC
CCTCTCCTCAGTTTTGCCGACTGCTTTTCATCTGAGTCACCATTTACTCCAAGCATGTATTCCAGACTTGTCACTGACTT
TCCTTCTGGAGCAGGTGGCTAGAAAAAGAGGCTGTGGGCA
>known_FOXA2_binding_decrease-ReferenceAllele__chr10_23507864_23508863
CTTCTTTTTATCTCTTAACTAACTTACAATTTCTTACGTGATTTTAAAACTTGTTTTTCTATTTAAAACAACAGGGGCAA
CTGAACTTCACTTTCAAACAATATTTATTTCTATAAATCAGTGCAAAACATACTTATTGAAAATATATCTTGGGTCCAAG
GCTTCAAAGGGTAAAAAGAAAGATTTTAAATTATATCTAATATGTTACAATTGTTCTGTCCTTTAAAAACCTTTTCAGAT
CACCCCCTGGATGATTCTTCCCTAGAAGTCTCAGAGAATTAACAACACAATGTAATCTAGGTTTAAATTTGGGTTTCTCC
TGTGTTTCAGATACTGATGTTTGAGCTTTCTCTTCCTGACAAGCCACTTAAAGAGTCACTGTTACTTTGAGGTTTTATCT
GTAAGATTCGTGTCTTTTGGGCTCATTAAGAACATTTCCAAAGATTACAATGTCAATAGCACCTAATTACTGGACTGTGA
GAAAGGTCTTCTTGAGTACATAAAATCTGTGGCAGTGCACAGTACACAATGGGCAGCTCAGATCCCAAATTTTATCACAA
GTAAGTAGCAAACAAATTAATAATGTTACCTGTGCTCTCTTGGATAATTACTACTGCATAAAAACTGCTTTGAAATGTTG
CAGATAGTATTGTACCTCATTTTTTTAATCCCCTTAGAGTAACAAGGATTTATTTGTCTCAAACTTTCTATGTTGCATGC
ACCACTTGACTTTCTTGTTCTGTTTAGAATTTTTAGAACTTGCAACATAACAAAAAATCATTTTTAACCAGCCTAGGAAG
GACATATCACCTGATGTAACATTATTTTAAATTATATTTTGTATTTTACTTTACTCTTTTCAAAACATATACTGTATGTT
TTGATACTATTGCTAGATTTTATTTTTTACTTATGCCTGGTAGAAAATCAGCTATTAAAGAAGCAGAGGAGGCTGGACAC
AGTGGTTCATGTCTGTAATCGCTAGCACTTTGAAAGAGTA
>known_GATA1_binding_increase-TtoC__chr16_209210_210209
GGGCTTAGACAGAGGAGGGGAGGATTCAGATTTTAAATGGGTTGGCCACTGTAGGTCTATTAACGTGGTGACATTTGAGG
GAGTGGCAATACTAGGGAAGGGGCTTCAGGGGAGTGGCCAGGAGCTAGGGATAGAGGGAGGGAGGACAGGAGGCCTTGTC
TGTCTTTTCCTCCATATGTAAGTTTCAGGAGTGAGTGGGGGGTGTCGAGGGTGCTGTGCTCTCCGGCCTGAGCCTCAGGA
AGGAAGGGCAGTAGTCAGGGATGCCAGGGAAGGACAGTGGAGTAGGCTTTGTGGGGAACTTCACGGTTCCATTGTTGAGA
TGATTTGCTGGAGACACACAGATGAGGACATCAAATACATCCCTGGATCAGGCCCTGGGGCCTGAGTCCGGAAGAGAGGT
CTGTATGGACACACCCATCAATGGGAGCACCAGGACACAGATGGAGGCTAATGTCATGTTGTAGACAGGATGGGTGCTGA
GCTGCCACACCCACATTATCAGAAAATAACAGCACAGGCTTGGGGTGGAGGCGGGACACAAGACTAGCCAGAAGGAGAAA
GAAAGGTGAAAAGCTGTTGGTGCAAGGAAGCTCTTGGTATTTCCAATGGCTTGGGCACAGGCTGTGAGGGTGCCTGGGAC
GGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCAGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCTGGACGGCTTG
TGGGGCACAGGCTGTGAGAGGTGCCCAGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCCGGGACGGCTTGTGGGGCA
CAGGTTGTGAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTTCAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTG
TGAGAGGTGCCCGGGACGGCTTGTGGGACACAGGTTGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGG
TGCCTGGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGC
>known_FOXA1_binding_increase-CtoT_chr16_52598689_52599688
GGCTCAAGCAGTCCTCCCATCTAGGCTTCCCAAAATGCTGGGATTACAGACATGAGCCACTGCACCCAGCCACAAAGATA
ACCTAAAGATGTGTTTACTTTGACCCAGGCAGTAGTTTAAAAAAGTTTTAATTTGTTGTTCACATTTAAAAACTGGACAA
TTTCTACATAAAAATCTGAATTACTCATGTCTCTTAAAAAAATAACATCTAGCAATGGTAGGCCCACATTCCTTCCTGAA
AATAATTAGCTGGGAAAGAGTAGGGACTGACCCCTTTAGACACGGTATAAATAGCATGGGAGTTGATCAGTAAATATTTG
CTGAATGAAAGAATACATGAATGAAAAGTCAGAGCCCTATAGGTCAGCATGGACGGCGGTAAAGGAACCTGGCTGAGCCT
GAAAGAGAATGTGATCTAAGATTAAATCCAGGATATGCTGGTAAATGTTTAACAGCCAACTCTTTGGGGAGGAAAAAAGT
CCCAATTTGTAGTGTTTGCTGATTATTGTGATGTAAATACTCCCATCATGACCAATTTCAAGCTACCAACATGCTGACAC
TGAACTTGGAGTTGGAAGGAGATGAACAGGCATAATCAGGTCTCGTGAGATGGCCCAAGCCGGCCCCAGCACTCCACTGT
TATATATGAGGCTAGAATTACTACATAACTGGAATAGCAACTTTCTGGACCATATGCCTGGAACACAGCAGGTGCTGAAT
AAATGTTTGTTGATCCAGGAACTGACTGTGTTGAAGCCCACAGATGGGAAATCAGTAGAAGGCAGGTAAGAGTAAAAAGA
AGGGCAGAGAATTGGGGGTACAGACCCCTGAACCATAAGTCAGAGGAATGTTGTACATGTTTTCAGATCCCTCACTGGTC
AAATGAAGGCAAAGGGTTAGATCTCTCCAAATCTTTAGAGGGACATGATGTAACTCCATTAAGTAACTCAGTGATTTTCA
ACATTAAAAAGTGTAATTATCTTTTCAAACTAAATATTAC`;

// FASTA Example for 2000bp sequences (Beluga/ISM)
export const FASTA_EXAMPLE_TEXT_2000BP = `>known_CEBP_binding_increase-GtoT__chr1_109816591_109818590
TCAACTAACCCTCTGTCTGCCTTTTCTGCCACTTTCCTTTCCTGCCTCTCCAGATTTCTCTTCTTTAACT
TCCTGCATTAACCCTGGGCCGTGGTTCCTACGCCCGAGGCTCCCTTCCCTTCCCCAGCCGCACTCATGCC
CTGCTCCTGTCTTGTGCTTTATCCTGCCCCGCTCCCCATCGCCTGCCCGCAGCAGCGACGAAACGTCCAT
CTGAGGAGCCTGGGCCTTGCCGGGAGGGGTACTCACCCCACCTAAGGCCATCTAGTGCCAACTCCCCCCC
CACCATTCCCCTCACTGCACTTTGGACCCCTGGGGCCAACATCTCCAAGACAAAGTTTTTCAGAAAAGAG
GAAAAAAAGAATTTAAAAAAGGATCTCCACTCTTCATGACTTCAGGGATTCATTTTTTTTATACGCTGGA
AATTGACTCCCCTTTCCCTTCCCAAAGAGGATAGGACCTCCCAGGATGCTTCCCAGCCTCTCCTCAGTTT
CCCATCTGCTGTGCCTCTGGGAGGAGAGGGACTCCTGGGGGGCCTGCCCCTCATACGCCATCACCAAAAG
GAAAGGACAAAGCCACACGCAGCCAGGGCTTCACACCCTTCAGGCTGCACCCGGGCAGGCCTCAGAACGG
TGAGGGGCCAGGGCAAAGGGTGTGCCTCGTCCTGCCCGCACTGCCTCTCCCAGGAACTGGAAAAGCCCTG
TCCGGTGAGGGGGCAGAAGGACTCAGCGCCCCTGGACCCCCAAATGCTGCATGAACACATTTTCAGGGGA
GCCTGTGCCCCCAGGCGGGGGTCGGGCAGCCCCAGCCCCTCTCCTTTTCCTGGACTCTGGCCGTGCGCGG
CAGCCCAGGTGTTTGCTCAGTTGCTGACCCAAAAGTGCTTCATTTTTCGTGCCCGCCCCGCGCCCCGGGC
AGGCCAGTCATGTGTTAAGTTGCGCTTCTTTGCTGTGATGTGGGTGGGGGAGGAAGAGTAAACACAGTGC
TGGCTCGGCTGCCCTGAGGTTGCTCAATCAAGCACAGGTTTCAAGTCTGGGTTCTGGTGTCCACTCACCC
ACCCCACCCCCCAAAATCAGACAAATGCTACTTTGTCTAACCTGCTGTGGCCTCTGAGACATGTTCTATT
TTTAACCCCTTCTTGGAATTGGCTCTCTTCTTCAAAGGACCAGGTCCTGTTCCTCTTTCTCCCCGACTCC
ACCCCAGCTCCCTGTGAAGAGAGAGTTAATATATTTGTTTTATTTATTTGCTTTTTGTGTTGGGATGGGT
TCGTGTCCAGTCCCGGGGGTCTGATATGGCCATCACAGGCTGGGTGTTCCCAGCAGCCCTGGCTTGGGGG
CTTGACGCCCTTCCCCTTGCCCCAGGCCATCATCTCCCCACCTCTCCTCCCCTCTCCTCAGTTTTGCCGA
CTGCTTTTCATCTGAGTCACCATTTACTCCAAGCATGTATTCCAGACTTGTCACTGACTTTCCTTCTGGA
GCAGGTGGCTAGAAAAAGAGGCTGTGGGCAGGAAAGAAAGGCTCCTGTTTCTCATTTGTGAGGCCAGCCT
CTGGCTTTTCTGCCGTGGATTCTCCCCCGTCTTCTCCCCTCAGCAATTCCTGCAAAGGGTTAAAAATTTA
ACTGGTTTTTACTACTGATGACTTGATTTAAAAAAAATACAAAGATGCTGGATGCTAACTTGGTACTAAC
CATCAGATTGTACAGTTTGGTTGTTGCTGTAAATAGGGTAGCGTTTTGTTGTTGTTGTTTTTTCATGCCC
CATACTACTGAATAAACTAGTTCTGTGCGGGTACAGCACTGTCACTGTCCGCTTCTGTGTGGTACCCTGT
CCTCCCACAGGGAGGAAGGTCTGGTCTCTGGAAAACAGAAGAGCCGGCAGGGCCCAGAAGGCCCCACCGG
GAGGTCGGGAGACTCAGACACCACTGGCTGATAAGCCTGTCCCTCTGCCCATGTCCATGACACTGCTCCC
AAATAGCTGGATCAGCACAGAGCCCATTTCCTGGCATTTC
>known_FOXA2_binding_decrease-ReferenceAllele__chr10_23507364_23509363
ACCACTCCCAGCCCCAGGTTTTAATTTATCATCTAATATGCTAAATTATATGCCTCAAGTATTAATAGAA
AATATCTCTAAATAAATGTATAAAAAGCAAGGGAAATTCCTAAGAAAAGGTAGTTGAGAATTAGCTCAAG
TCTCAAAGACAGACATGACATGTACAACCTAGAAATATAAAATTTGAATAATAGGTAACTGATGGTAACA
AGGGAAGTAATGGAAAAGTTGGTGGCAGTGACATTAACATTTACAGACCCCGTAATACATTATATTTAAT
AAATCAGGTTAGTCCTGGTATTAACTCACAATTCATTGATTAACTCCTTCTAACATTTTTATGGCCATCA
ATTATATGTGGAATCTATTGTTTGGATATACATTGTAATATTATAAAATGTGCACTTGAATATGAATCTG
AAAACCTTAACAAATTATATCCATTATAGCTGACAGCCTAGAGACAATATATAAAACAATGTTAACACGT
ATGCCTGAAACTTCTTTTTATCTCTTAACTAACTTACAATTTCTTACGTGATTTTAAAACTTGTTTTTCT
ATTTAAAACAACAGGGGCAACTGAACTTCACTTTCAAACAATATTTATTTCTATAAATCAGTGCAAAACA
TACTTATTGAAAATATATCTTGGGTCCAAGGCTTCAAAGGGTAAAAAGAAAGATTTTAAATTATATCTAA
TATGTTACAATTGTTCTGTCCTTTAAAAACCTTTTCAGATCACCCCCTGGATGATTCTTCCCTAGAAGTC
TCAGAGAATTAACAACACAATGTAATCTAGGTTTAAATTTGGGTTTCTCCTGTGTTTCAGATACTGATGT
TTGAGCTTTCTCTTCCTGACAAGCCACTTAAAGAGTCACTGTTACTTTGAGGTTTTATCTGTAAGATTCG
TGTCTTTTGGGCTCATTAAGAACATTTCCAAAGATTACAATGTCAATAGCACCTAATTACTGGACTGTGA
GAAAGGTCTTCTTGAGTACATAAAATCTGTGGCAGTGCACAGTACACAATGGGCAGCTCAGATCCCAAAT
TTTATCACAAGTAAGTAGCAAACAAATTAATAATGTTACCTGTGCTCTCTTGGATAATTACTACTGCATA
AAAACTGCTTTGAAATGTTGCAGATAGTATTGTACCTCATTTTTTTAATCCCCTTAGAGTAACAAGGATT
TATTTGTCTCAAACTTTCTATGTTGCATGCACCACTTGACTTTCTTGTTCTGTTTAGAATTTTTAGAACT
TGCAACATAACAAAAAATCATTTTTAACCAGCCTAGGAAGGACATATCACCTGATGTAACATTATTTTAA
ATTATATTTTGTATTTTACTTTACTCTTTTCAAAACATATACTGTATGTTTTGATACTATTGCTAGATTT
TATTTTTTACTTATGCCTGGTAGAAAATCAGCTATTAAAGAAGCAGAGGAGGCTGGACACAGTGGTTCAT
GTCTGTAATCGCTAGCACTTTGAAAGAGTATGTATGACAGCAGGATAGCTTGAGCCCAGGAGTTGGAGAC
CAGTCTAGATGACAAAGTGAGACCCTGTCTTTACAAAAAATAAAGAAATTAGCTTGATGTGGTGGTGTGT
GCCTGTAGTTCCAGCTACTCAGAAGGCTGAGGCAGGAGGATCACCTGGGCCCAGGAATTTGAGACTGCAG
TGAGCTATGACTGGACCACTGCACTCCAGCCTGGATGACAGACAAATACCCCATCTCAAGAAAAAAAAAA
GCAGTAGAGATTAGCTGTTTTAATTTATTAGCCTACTGTTTAGGGCATCATTGCTTTCACGGGGAGATTA
AAATATAGATTTGCAAGGCTTTAGCTTGGGATTATACTATGAACAGCTCAAGAGGACAGGTAGAGAGAGG
AATTAGCTAGGTATGTCACATATATGATCTCACTTCATCCTCATAACAAACCTATGAGGTGGGGTTGCCG
TGTCTGTTTTACACAACAGGAAGCACTAAGATGAAGGCAC
>known_GATA1_binding_increase-TtoC__chr16_208710_210709
GGAAATGGATGTTCTTTAAAATATGTATTTATTTATTTTTCTTTTTTGAGACGGAATCTTGTTCTGTCGC
CCAGGCTGGAGGGCAGTGGGACAATCTTGGTTCACTGCAACCTCTGCCTCCTGGGTTCAAGAGGTTCTCC
TGCCTCAGCCTCCCGAGTAGCTGGGATTACAGGCGCGAACCACCACGCCCGGTTAATTTTGGTATTTTTA
GTAGAGGCGGGGTTTTACCATGTTGGCCAGGCTGGTCTCAAACTCCTGACATGGTAGCCTGTAATCCCAG
CTACTCGGGAGGCTGAGGCAGGAGAATCGCTTGAACCCAGGAGGTGGGGTTGCAGTGAGCCAAGATCGTG
CCATTGCACTCCAGCCTGGGAGACAGAGCGAGACTCCATCAAAAAAAAAAAAAAAAAAAATTCCTGAAGC
TCCTCTTGAGCTTACATTCTAGTGGACTGTAAACAGAAACATTTTTTTTTCCTGTGGATAAAGAAAAGCA
GGGCAAGTAGGGGCTTAGACAGAGGAGGGGAGGATTCAGATTTTAAATGGGTTGGCCACTGTAGGTCTAT
TAACGTGGTGACATTTGAGGGAGTGGCAATACTAGGGAAGGGGCTTCAGGGGAGTGGCCAGGAGCTAGGG
ATAGAGGGAGGGAGGACAGGAGGCCTTGTCTGTCTTTTCCTCCATATGTAAGTTTCAGGAGTGAGTGGGG
GGTGTCGAGGGTGCTGTGCTCTCCGGCCTGAGCCTCAGGAAGGAAGGGCAGTAGTCAGGGATGCCAGGGA
AGGACAGTGGAGTAGGCTTTGTGGGGAACTTCACGGTTCCATTGTTGAGATGATTTGCTGGAGACACACA
GATGAGGACATCAAATACATCCCTGGATCAGGCCCTGGGGCCTGAGTCCGGAAGAGAGGTCTGTATGGAC
ACACCCATCAATGGGAGCACCAGGACACAGATGGAGGCTAATGTCATGTTGTAGACAGGATGGGTGCTGA
GCTGCCACACCCACATTATCAGAAAATAACAGCACAGGCTTGGGGTGGAGGCGGGACACAAGACTAGCCA
GAAGGAGAAAGAAAGGTGAAAAGCTGTTGGTGCAAGGAAGCTCTTGGTATTTCCAATGGCTTGGGCACAG
GCTGTGAGGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCAGGACGGCTTGTGGGGCA
CAGGTTGTGAGAGGTGCCCTGGACGGCTTGTGGGGCACAGGCTGTGAGAGGTGCCCAGGACGGCTTGTGG
GGCACAGGCTGTGAGGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGACGGCTTG
TGGGGCACAGGTTTCAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGACGGC
TTGTGGGACACAGGTTGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCTGGGAC
GGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGTCGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCG
GGACGGCTTGTGGGGCACAGGTTGTGAGACGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGC
CCGGGTCGGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGG
TGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCCGGGACAGCTCGTGGGGCACAGGTTGTGAG
AGGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCTGGGACGGCTTGTGGGGCACAGGTTGT
GAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGGATGCCCGGGATGGCTTGTGGGGCACAGGT
TGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCCGGGACGGCTTGTGGGGCACA
GGCTGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGC
>known_FOXA1_binding_increase-CtoT_chr16_52598189_52600188
GTGAGCATTGCTGGGGACCCCTACAAGGCCTTTAAGGAGAGGACATTGTTTTAAACACACATCTAAAACT
CACTACTTACATGACAGACATAGGGCAGAGGGTGGAGTACTAGGCGTGTGCCTGGGTTTCCAGGTGGCTG
AACTGTAGCATGACTGGCGGGACTGAAGGAGTGGAGAGGCTAAGGTCTTGATGTCACCTACTTTTGTTAG
GATTAGAACAGGGGTCACAAACTGTTGGACCCCATGGCAAATGTAATTTTCTTTTTCAATTTTTTATTTT
TTGAGACAGTCTCACTCTGTCACACAGGCTAGAGTGCAGTGGCATGATCTCAGCTCGCGGCAGCCTCAAA
CTCCCTGGCTCAAGCCATGCTCCCCCACCTCAGCCTCCTGAGTAGCTGGGACTACAGGCACATGCCACTC
CACCCGGCTAATTTTTTTTCTTTTTCTTTTTTGTAGAGATGGGGTCTCACCTTGTTGTTCAGGTTGATCT
CGAATTCTTGGGCTCAAGCAGTCCTCCCATCTAGGCTTCCCAAAATGCTGGGATTACAGACATGAGCCAC
TGCACCCAGCCACAAAGATAACCTAAAGATGTGTTTACTTTGACCCAGGCAGTAGTTTAAAAAAGTTTTA
ATTTGTTGTTCACATTTAAAAACTGGACAATTTCTACATAAAAATCTGAATTACTCATGTCTCTTAAAAA
AATAACATCTAGCAATGGTAGGCCCACATTCCTTCCTGAAAATAATTAGCTGGGAAAGAGTAGGGACTGA
CCCCTTTAGACACGGTATAAATAGCATGGGAGTTGATCAGTAAATATTTGCTGAATGAAAGAATACATGA
ATGAAAAGTCAGAGCCCTATAGGTCAGCATGGACGGCGGTAAAGGAACCTGGCTGAGCCTGAAAGAGAAT
GTGATCTAAGATTAAATCCAGGATATGCTGGTAAATGTTTAACAGCCAACTCTTTGGGGAGGAAAAAAGT
CCCAATTTGTAGTGTTTGCTGATTATTGTGATGTAAATACTCCCATCATGACCAATTTCAAGCTACCAAC
ATGCTGACACTGAACTTGGAGTTGGAAGGAGATGAACAGGCATAATCAGGTCTCGTGAGATGGCCCAAGC
CGGCCCCAGCACTCCACTGTTATATATGAGGCTAGAATTACTACATAACTGGAATAGCAACTTTCTGGAC
CATATGCCTGGAACACAGCAGGTGCTGAATAAATGTTTGTTGATCCAGGAACTGACTGTGTTGAAGCCCA
CAGATGGGAAATCAGTAGAAGGCAGGTAAGAGTAAAAAGAAGGGCAGAGAATTGGGGGTACAGACCCCTG
AACCATAAGTCAGAGGAATGTTGTACATGTTTTCAGATCCCTCACTGGTCAAATGAAGGCAAAGGGTTAG
ATCTCTCCAAATCTTTAGAGGGACATGATGTAACTCCATTAAGTAACTCAGTGATTTTCAACATTAAAAA
GTGTAATTATCTTTTCAAACTAAATATTACAAATAAATTCTGATAAAAGGAAATGTACGTCTGTTGAGAC
CCCTGATCCTATAACTGGTTTAGTATTCCCTGAAGCCACTTGTAGTAGCCTATTCACATGGGGACTCCCC
AGTGAACATTTCCTCATATCCACACTCTTGTGAAGGTCCTTCACCTTGAATCTGGACTGGGCCTATGGCC
TGATTTGATGCATAGAATGCACAGATGAGAATAGGTGGGTTTCTGGCATAAGCTCAAAGAAGGCACAGTG
GCTTCCAGAGCCCTGAGCTACTAGTAAGAAAGTCCAGCTACCCTGCTGGGAAGGCCATGGAGAGAGGCCA
TGGGGAGCTCATAGAGAAAGAGAGGGACCCTGAGACTACATGGAGCTAGGAAGAACAACACCCAGCCATC
CCAGTGTCCCAGCTGAGCCCCACTCCAAACCCATCTGCCAACTGAGCACATCTACAAGAGTAACCACTGA
CAGGCCAGCACAAGCCCAGCCAAGTCCAGTTCAAATTGCA`;

// FASTA Example for 4096bp sequences (Sei)
export const FASTA_EXAMPLE_TEXT_4096BP = `>known_CEBP_binding_increase-GtoT__chr1_109815543_109819638
GGAAGAAGAGGAGGAGGAAGAGGAGGCCGCCTTCCCTGGAGAGCAGGGCTGGGATAGCCTGCTGGGGCCT
GGAGCAGAGAGACTGCCCCTGCACAGTACTCCCAAGGGTGGGCCAGCACTGGGGCTGTGGCCTTTGGGGC
CAGTGGGAGGACAGTGGGCCTGGGGTTCTTGGGAAGCAGGACTGGGGTGGTGGCCTCCTGGCTATCTGCC
CTCTGTGGGCCTCATCTACTTCCTTTCCCCACCAGATGGGGGCCCAGGGCCTGGCAAGGCCCCCTGGCCA
GGAGACTTTGGGACCACAGCAAAAGAGAGTAGTGGCAACGGGGCCCCTGAGGAGCGGCTGCGGGAGAATG
GAGATGCCCTGTCTCGAGAGGGGTCCCTAGGCCCCCTTCCAGGCTCTTCTGCCCAGCCTCACAAAGGTGA
GTGGGGCACCCCCAGCTGCCGAGCTCCCCTAGTCAGCAGCCTCATACCTCACATTCTCCTGTGGCCGCAC
CTCACAGCCCCGCCCCGGCCCACAGGCATCCTTAAGAAGAAGTGTCTGCCCACCATCAGCGAGAAGAGCA
GCCTCCTGCGGCTCCCCCTGGAGCAATGCACAGGGTCTTCCCGGGGCTCCTCCGCTAGTGAGGGCAGCCG
GGGAGGCCCCCCTCCCCGCCCACCGCCCCGGCAGAGCCTCCAGGAGCAGCTGAACGGGGTCATGCCCATC
GCCATGAGCATCAAGGCAGGCACGGTGGATGAGGACTCGTCAGGCTCCGAGTGAGTGTGGCCGGGTGGGC
GGGACGGGGTGCAGCCCCTCGGAGGCCTCACCTGGGCCCAGAGCTGCCTCCGGGCCTCCCCAGGTGACTC
TGGATGGCATTTGGAGGACAAATGGGCAGAACTGGCCCAGCTCCCTTACTATGCCGTGCCCACAAACCAT
GGTGCCTTGCGGGGAGGGCCACCCCTCCTAGGCTCTACGCGGCGCAGCCCAGCCTAGGGCAGAGTGCGGG
AGGATGGGGAGCTGGGGGTGCTTTCCTGTTTCCTTCGTCTGGTGAAAGCTTTCACTCTCTCCTCTGTTTC
AACTAACCCTCTGTCTGCCTTTTCTGCCACTTTCCTTTCCTGCCTCTCCAGATTTCTCTTCTTTAACTTC
CTGCATTAACCCTGGGCCGTGGTTCCTACGCCCGAGGCTCCCTTCCCTTCCCCAGCCGCACTCATGCCCT
GCTCCTGTCTTGTGCTTTATCCTGCCCCGCTCCCCATCGCCTGCCCGCAGCAGCGACGAAACGTCCATCT
GAGGAGCCTGGGCCTTGCCGGGAGGGGTACTCACCCCACCTAAGGCCATCTAGTGCCAACTCCCCCCCCA
CCATTCCCCTCACTGCACTTTGGACCCCTGGGGCCAACATCTCCAAGACAAAGTTTTTCAGAAAAGAGGA
AAAAAAGAATTTAAAAAAGGATCTCCACTCTTCATGACTTCAGGGATTCATTTTTTTTATACGCTGGAAA
TTGACTCCCCTTTCCCTTCCCAAAGAGGATAGGACCTCCCAGGATGCTTCCCAGCCTCTCCTCAGTTTCC
CATCTGCTGTGCCTCTGGGAGGAGAGGGACTCCTGGGGGGCCTGCCCCTCATACGCCATCACCAAAAGGA
AAGGACAAAGCCACACGCAGCCAGGGCTTCACACCCTTCAGGCTGCACCCGGGCAGGCCTCAGAACGGTG
AGGGGCCAGGGCAAAGGGTGTGCCTCGTCCTGCCCGCACTGCCTCTCCCAGGAACTGGAAAAGCCCTGTC
CGGTGAGGGGGCAGAAGGACTCAGCGCCCCTGGACCCCCAAATGCTGCATGAACACATTTTCAGGGGAGC
CTGTGCCCCCAGGCGGGGGTCGGGCAGCCCCAGCCCCTCTCCTTTTCCTGGACTCTGGCCGTGCGCGGCA
GCCCAGGTGTTTGCTCAGTTGCTGACCCAAAAGTGCTTCATTTTTCGTGCCCGCCCCGCGCCCCGGGCAG
GCCAGTCATGTGTTAAGTTGCGCTTCTTTGCTGTGATGTGGGTGGGGGAGGAAGAGTAAACACAGTGCTG
GCTCGGCTGCCCTGAGGTTGCTCAATCAAGCACAGGTTTCAAGTCTGGGTTCTGGTGTCCACTCACCCAC
CCCACCCCCCAAAATCAGACAAATGCTACTTTGTCTAACCTGCTGTGGCCTCTGAGACATGTTCTATTTT
TAACCCCTTCTTGGAATTGGCTCTCTTCTTCAAAGGACCAGGTCCTGTTCCTCTTTCTCCCCGACTCCAC
CCCAGCTCCCTGTGAAGAGAGAGTTAATATATTTGTTTTATTTATTTGCTTTTTGTGTTGGGATGGGTTC
GTGTCCAGTCCCGGGGGTCTGATATGGCCATCACAGGCTGGGTGTTCCCAGCAGCCCTGGCTTGGGGGCT
TGACGCCCTTCCCCTTGCCCCAGGCCATCATCTCCCCACCTCTCCTCCCCTCTCCTCAGTTTTGCCGACT
GCTTTTCATCTGAGTCACCATTTACTCCAAGCATGTATTCCAGACTTGTCACTGACTTTCCTTCTGGAGC
AGGTGGCTAGAAAAAGAGGCTGTGGGCAGGAAAGAAAGGCTCCTGTTTCTCATTTGTGAGGCCAGCCTCT
GGCTTTTCTGCCGTGGATTCTCCCCCGTCTTCTCCCCTCAGCAATTCCTGCAAAGGGTTAAAAATTTAAC
TGGTTTTTACTACTGATGACTTGATTTAAAAAAAATACAAAGATGCTGGATGCTAACTTGGTACTAACCA
TCAGATTGTACAGTTTGGTTGTTGCTGTAAATAGGGTAGCGTTTTGTTGTTGTTGTTTTTTCATGCCCCA
TACTACTGAATAAACTAGTTCTGTGCGGGTACAGCACTGTCACTGTCCGCTTCTGTGTGGTACCCTGTCC
TCCCACAGGGAGGAAGGTCTGGTCTCTGGAAAACAGAAGAGCCGGCAGGGCCCAGAAGGCCCCACCGGGA
GGTCGGGAGACTCAGACACCACTGGCTGATAAGCCTGTCCCTCTGCCCATGTCCATGACACTGCTCCCAA
ATAGCTGGATCAGCACAGAGCCCATTTCCTGGCATTTCCAAGGAAGACATGGTGGGCGGGCAGGAAGGTG
TCCTTTTCACCAGGACGGAAAGGACTTCTACGGTGGGAGAGGCTGCACGGGGCAGGCAGCCTTCCAGCCC
AGCCCCTCAGGGCTGAGTCCTAGACAGGGATGAGTTTTCTGGGGCAGAATTCAAGAAGCAGGGAGGGCTG
CATTGAGAGGCTGGTTGGAATTGAATGGTGGTCAGTGACTCAGTGCCTCCTAACTATACTGCTCAGGGTT
GTTTTTTGTTTTTTTTTTCCCATTGGAAAATGAAGCAGCTACTCATACTGAAGCCAAGTGTGTGCTTTGC
AACATTACTTCTCAGCCTAAGTTTTGTTTTTGTGTATATATATGATATATATTTAAAAGTATATATGCTT
TTTAAATTTAATAGAGGCGGGGTCTCACTATGTTGATCAGGCTGGTCTTGAATTCCTAGGCTCAAGTGAT
CCTCCCGCCTTGGCCTCCCAAACTGGGGTCACAGATATGAGCTACCGCATCCGGCCTAGTTCCAAGTTTT
GTGTTTGTCGAAGTTCCATTCAATAAACTAGAAAGGCAGCTGGGTGCGGTGGCTCATGCCTGTAATCCCA
GCACTTTGGGAGGCCGAGGTGGACAGATCACCTGAGATCAGGAGTTTGAGACTAGCCTGGGCAACATGGT
GAAACTCTGTCTCTACTAAAAATACAAAAAAATTAGCTGGGTGCGGTGGTACATGCCTGTAATCGCAGCT
ACTCCAGGGGCTGAGACAGGAGAATCACTTGAACCTGGGAGGTGGAGATTGCAGTGAGCTGAGATCGTGC
CACTGCACTCCAGCCTGGGTGACAGAGCGAGGCTCCATCTCAAAACAAACAAAAAAACTAGAAAGGGAGT
TAACCTGGGGACAATATAGGGTGATGATTAAGAACACAAACCCTAGCTGGGCAGTGGTTCACGCCTGTAA
TCCCAATACTTTGGGAGGCCAAGGCATGTGGATCACTTGAGGCCAGGAGTTAAGAGACCAGCCTGGCCAA
CATGGTGAAACCCATATCTACAAAAAATACAAAAAA
>known_FOXA2_binding_decrease-ReferenceAllele__chr10_23506316_23510411
AAGCCCATGGATTTGCATGAATCCATGTATAATATCCTGTAACATTTTGCCAGCAGACTCATTCCTAAAT
TAAAAAGTTAATACCCACTTGTCTCTGCACTAATTAAACACTTTCTCAGTGTTTTGTTACTACCATAATG
GCTTATGTGGTTTATAATGCCTCTTAAATGAACACAATATCAGTCTGCAACCAGGCAGCAAGGTTTGGGA
TGAGATCATTGTCACAGGGTAGAGAGGCTGCTGGAATCCAACTCATGGCTCAACTGGACTAACAACACAC
ACCAGAAACATTCTTACCTTCTGCCTCATGCTTCAGAGAGCTTTAAGCACCAGGTGTAGGTGTGATAAGG
AATGCGATTAGTGGTATTTATTAAATGAAATATTGTCTATGGATGCTTAAAAACATATTAGACTGAAATA
AGACAGTCCATCAAATTAATAAGGGACATTTTAATAGTTATTAGCCTAAACTTTTTTCTACTAAATTTTT
ATGATCTTGCAAATTCAAAGTAATTACCTGCCTATTTATTTCAAAGATTAACACTGTATTTTCAAAGCAT
ATGGGTTGCCTTCCCTTTTCATTTCTAACATTTTTCAATCCCAATCTGACCTACGGACAGAGCTTCAGTT
ACAGAGAGAGCCTCTTATTTTTGTCATCACTTCTCTAATCCAGTTGGTCATCCTAACCAATTTCTATACC
ATTCGGAATTCACTGTATTTGGAAGGAAAAATTGAGAGACAGGCTGCTGATCTTCCAGGTTTTTATTTGT
TTGTGTTGAGACAGTCTTTGTTGCCCAGGCTGAAGTGCAGTGGCGCCATCATGGCACACTGCAGCCTCCA
CCTCCCTGGGCTCAGGTGATCTTCCCACCTCAGCTTCCCTAGTAGCTGGGACTACAGGTTCACATCACTA
CACGAGGCTAATTTTTTTTTTTTTTTTTTTTTGTAGAGACGGGGCTTCCCCATGTTGCTCAGGTGGTTCT
CAAACCCCTGAGCACAACATATCCACCTGCCTTGGCCTCCCAAAGTATTGGGATTACAGGTGTGAGCCAC
CACTCCCAGCCCCAGGTTTTAATTTATCATCTAATATGCTAAATTATATGCCTCAAGTATTAATAGAAAA
TATCTCTAAATAAATGTATAAAAAGCAAGGGAAATTCCTAAGAAAAGGTAGTTGAGAATTAGCTCAAGTC
TCAAAGACAGACATGACATGTACAACCTAGAAATATAAAATTTGAATAATAGGTAACTGATGGTAACAAG
GGAAGTAATGGAAAAGTTGGTGGCAGTGACATTAACATTTACAGACCCCGTAATACATTATATTTAATAA
ATCAGGTTAGTCCTGGTATTAACTCACAATTCATTGATTAACTCCTTCTAACATTTTTATGGCCATCAAT
TATATGTGGAATCTATTGTTTGGATATACATTGTAATATTATAAAATGTGCACTTGAATATGAATCTGAA
AACCTTAACAAATTATATCCATTATAGCTGACAGCCTAGAGACAATATATAAAACAATGTTAACACGTAT
GCCTGAAACTTCTTTTTATCTCTTAACTAACTTACAATTTCTTACGTGATTTTAAAACTTGTTTTTCTAT
TTAAAACAACAGGGGCAACTGAACTTCACTTTCAAACAATATTTATTTCTATAAATCAGTGCAAAACATA
CTTATTGAAAATATATCTTGGGTCCAAGGCTTCAAAGGGTAAAAAGAAAGATTTTAAATTATATCTAATA
TGTTACAATTGTTCTGTCCTTTAAAAACCTTTTCAGATCACCCCCTGGATGATTCTTCCCTAGAAGTCTC
AGAGAATTAACAACACAATGTAATCTAGGTTTAAATTTGGGTTTCTCCTGTGTTTCAGATACTGATGTTT
GAGCTTTCTCTTCCTGACAAGCCACTTAAAGAGTCACTGTTACTTTGAGGTTTTATCTGTAAGATTCGTG
TCTTTTGGGCTCATTAAGAACATTTCCAAAGATTACAATGTCAATAGCACCTAATTACTGGACTGTGAGA
AAGGTCTTCTTGAGTACATAAAATCTGTGGCAGTGCACAGTACACAATGGGCAGCTCAGATCCCAAATTT
TATCACAAGTAAGTAGCAAACAAATTAATAATGTTACCTGTGCTCTCTTGGATAATTACTACTGCATAAA
AACTGCTTTGAAATGTTGCAGATAGTATTGTACCTCATTTTTTTAATCCCCTTAGAGTAACAAGGATTTA
TTTGTCTCAAACTTTCTATGTTGCATGCACCACTTGACTTTCTTGTTCTGTTTAGAATTTTTAGAACTTG
CAACATAACAAAAAATCATTTTTAACCAGCCTAGGAAGGACATATCACCTGATGTAACATTATTTTAAAT
TATATTTTGTATTTTACTTTACTCTTTTCAAAACATATACTGTATGTTTTGATACTATTGCTAGATTTTA
TTTTTTACTTATGCCTGGTAGAAAATCAGCTATTAAAGAAGCAGAGGAGGCTGGACACAGTGGTTCATGT
CTGTAATCGCTAGCACTTTGAAAGAGTATGTATGACAGCAGGATAGCTTGAGCCCAGGAGTTGGAGACCA
GTCTAGATGACAAAGTGAGACCCTGTCTTTACAAAAAATAAAGAAATTAGCTTGATGTGGTGGTGTGTGC
CTGTAGTTCCAGCTACTCAGAAGGCTGAGGCAGGAGGATCACCTGGGCCCAGGAATTTGAGACTGCAGTG
AGCTATGACTGGACCACTGCACTCCAGCCTGGATGACAGACAAATACCCCATCTCAAGAAAAAAAAAAGC
AGTAGAGATTAGCTGTTTTAATTTATTAGCCTACTGTTTAGGGCATCATTGCTTTCACGGGGAGATTAAA
ATATAGATTTGCAAGGCTTTAGCTTGGGATTATACTATGAACAGCTCAAGAGGACAGGTAGAGAGAGGAA
TTAGCTAGGTATGTCACATATATGATCTCACTTCATCCTCATAACAAACCTATGAGGTGGGGTTGCCGTG
TCTGTTTTACACAACAGGAAGCACTAAGATGAAGGCACCAGTGAGTAAGTAGTCACCTGAGCTTGAAAGC
TCTGTCAGCCTCCAAAGCCCAGGTTAGCTCTGTTACACCATGGATTTTCCACACTGTGTTTTATTGAAAA
TGGGATATTTTCCATGTTGTCATGCATGAATCTTGGGGCAGGTGAAAATAATTGAGTGGGGATTAGCATG
GACTCTTAGCCTACACTGGATTTGAATCCAGCTGCTCTACTTCTTTGCAATATAAACCTAACAGTTTACC
AACCTCTCTGTATTCAGTGGTCTTATCTGTAAAATGGGGGAAAGCAAGAGTACCCACCTAATAGGATTTT
TGTGAGGATTAAAGTAAAAACGTGAAAGGCATTTAGAATATTATGTATATCCACTGAATGATAGTTATAC
ATAATTTCACAAAGTATTTTGTGGTGTGAAAGTATAGCCTAAGAAGTTCAACCTTTGATGCCAATATGAG
GATTCTGTAACAGAAACGATAATATTCCATAGGATACTTTAAATAATAATGTTCCTATCCTATTTACATA
TTTAAAAATGGAATGCATTTTAACTTGAAAGTGTTCGGTTGGGATCTGTTACCAAATAGGTTATAGTAAC
CCCTTATGTTTTATGTACCTGTATTAGTCCATTCTCATGCTGCTATAAAGAACTACCTGAGACTGGCTAA
TTCATGGAGAAAAGAGGTTTAATTGACTGACAGTTTCACAGGCTTAACAGGACGCATGGCTAGGAGGTGT
CAGGAAACTTACAATCATGGTGGAAGGGGAAGTAAGCATGTCTTACAATGGAGGAGCAGGAGAGAGCGAG
AGAGAGAGACAGCGAAGTGCCACACACTTTTAAACCATCAGATCTCATGAGAACTTACTAGCATGAGAAG
AGTAAGGGGGAAATCAGCCCCTATAATCCAATCACCTCCCACCAGGCCCCTTCTCCAAATGGACGTGAGA
TTTGGGTGGGGACACAAATCCAAACCATATCAATATCTAATACCTCTTTAATAGGTATTTTTGAATTAGT
GCTTGAAAGCATTAATAATGTATTAAACTATTTCCG
>known_GATA1_binding_increase-TtoC__chr16_207662_211757
AAAAGGATTGTTCCAGGTGACCTCTGAACACAGAACCTCGGCTATGACCGAAAGAACTCCAAAGACACTC
TAACACTCCGTGGTTTATTGTTCCTCATAACATATATAAAATAATTTCATAAGCTTTTATTTTGAAACAT
ATTCAGATTATGAAGAAATAAAAACACCCTGCAAGAATAAGACAAAGATGGAGAAGGAAGGATGACTGCT
GGTGGGTTTGGGGCTTTTGGAGGGTGATGGAAACCTTCTAAAATTGATTATGGTGATGGTCGCACAATTA
TGTGAACACATTAAAAATTATTGAAATGGGCCGGGGGTGGTGGCTCACCCCTGTAATCCCAGCACTTTGG
GAGGCCAACGCGGGCAGATTACCTGAGCTCAGGAGTTCCAGACTAACCTGGCCAACATGGTGAAACCCCC
GTCCCTACTAAAAATGCAAAAATTAGCCACGCATGGTGGCACATGCCTGTAATCCCAGCTACTGGGGAGG
CTGAGGCAGGAGAATTGCTTGAACCCAGGAGACAGAGGTTGCAGTGAGCCGAGATTGTGCCACTGAACTC
CAGCTTGGCCGACAGAGTGAGACTCTGTCTCAAAAAAAAAAAAAATTATTGAAATGTACACATTAAGTGG
GTGAATTTTATCTCAATAAAACTGTTAAATAAAATAACAAGAATATGAAAAACTCTTGAATACTACTCAT
CCAGACTCTCCAGCTGTTAACATTCTACCACATCGGCTTGCTCTCTCTTGCCCCCACTTGCTCTTTCTCT
CGGAGCCCTTGGAGAGGGGTATGCAAATATCCGTACTCTAAATATCCTCCATATACTGTGTATTTCCTAA
AATCAACAAGGACATTAGGCTGCACAGCCAGAGAACAACCATCAAAATCAGGTTAATATTGATCCAAATC
CATCTATCAACAGAAGCAACATCAAGTTCAAGACCCTTTTGAAAGCAATGATACCAGCCATTTACTCCAT
CCCTAAAGGACTGAGGGTGCTGCGAATTTAACCGTATCAATGCAGTCTTTTTGATGTTATTTACTGAAGG
AAATGGATGTTCTTTAAAATATGTATTTATTTATTTTTCTTTTTTGAGACGGAATCTTGTTCTGTCGCCC
AGGCTGGAGGGCAGTGGGACAATCTTGGTTCACTGCAACCTCTGCCTCCTGGGTTCAAGAGGTTCTCCTG
CCTCAGCCTCCCGAGTAGCTGGGATTACAGGCGCGAACCACCACGCCCGGTTAATTTTGGTATTTTTAGT
AGAGGCGGGGTTTTACCATGTTGGCCAGGCTGGTCTCAAACTCCTGACATGGTAGCCTGTAATCCCAGCT
ACTCGGGAGGCTGAGGCAGGAGAATCGCTTGAACCCAGGAGGTGGGGTTGCAGTGAGCCAAGATCGTGCC
ATTGCACTCCAGCCTGGGAGACAGAGCGAGACTCCATCAAAAAAAAAAAAAAAAAAAATTCCTGAAGCTC
CTCTTGAGCTTACATTCTAGTGGACTGTAAACAGAAACATTTTTTTTTCCTGTGGATAAAGAAAAGCAGG
GCAAGTAGGGGCTTAGACAGAGGAGGGGAGGATTCAGATTTTAAATGGGTTGGCCACTGTAGGTCTATTA
ACGTGGTGACATTTGAGGGAGTGGCAATACTAGGGAAGGGGCTTCAGGGGAGTGGCCAGGAGCTAGGGAT
AGAGGGAGGGAGGACAGGAGGCCTTGTCTGTCTTTTCCTCCATATGTAAGTTTCAGGAGTGAGTGGGGGG
TGTCGAGGGTGCTGTGCTCTCCGGCCTGAGCCTCAGGAAGGAAGGGCAGTAGTCAGGGATGCCAGGGAAG
GACAGTGGAGTAGGCTTTGTGGGGAACTTCACGGTTCCATTGTTGAGATGATTTGCTGGAGACACACAGA
TGAGGACATCAAATACATCCCTGGATCAGGCCCTGGGGCCTGAGTCCGGAAGAGAGGTCTGTATGGACAC
ACCCATCAATGGGAGCACCAGGACACAGATGGAGGCTAATGTCATGTTGTAGACAGGATGGGTGCTGAGC
TGCCACACCCACATTATCAGAAAATAACAGCACAGGCTTGGGGTGGAGGCGGGACACAAGACTAGCCAGA
AGGAGAAAGAAAGGTGAAAAGCTGTTGGTGCAAGGAAGCTCTTGGTATTTCCAATGGCTTGGGCACAGGC
TGTGAGGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCAGGACGGCTTGTGGGGCACA
GGTTGTGAGAGGTGCCCTGGACGGCTTGTGGGGCACAGGCTGTGAGAGGTGCCCAGGACGGCTTGTGGGG
CACAGGCTGTGAGGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGACGGCTTGTG
GGGCACAGGTTTCAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGACGGCTT
GTGGGACACAGGTTGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCTGGGACGG
CTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGGTCGGCTTGTGGGGCACAGGTTGTGAGAGGTGCCCGGG
ACGGCTTGTGGGGCACAGGTTGTGAGACGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCC
GGGTCGGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTG
CCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCCGGGACAGCTCGTGGGGCACAGGTTGTGAGAG
GTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCTGGGACGGCTTGTGGGGCACAGGTTGTGA
GAGGTGCCCGGGACGGCTTGTGGGGCACAGGTTGTGAGGATGCCCGGGATGGCTTGTGGGGCACAGGTTG
TGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGGTGCCCGGGACGGCTTGTGGGGCACAGG
CTGTGAGAGGTGCCTGGGACGGCTTGTGGGGCACAGGCTGTGAGGATGCCCGGGACGGCTTGTGGGGCAC
AGGTTGTGAGGGGTGCCCAGGACGGCTTGTGGGGCACAGGCTGCAAGAGGTGCCCAGGACGGCTTGTGGG
GCACAGGTTGTGAGAGGTGCCCGGGACGGCTTGTGGGGCACAGGCTGTGAGGGAGCCCGGCACGGCTTGC
AGCTACAGGGAGAAAAGACTTGGTGCTGTGGGCCTGCCTTGGGGCTGGTGGTACAGCCCTTATCTGCTGC
CCTCAGGATCTCCCGGCCCCTCTCGTCCAGGCCCCTGCAACCCCATGCCCCAGCCTCTGAGGACCAAAGG
CGCCCCTGCTTGGGAAGAGGGGGCTCAGGGGAGTCGCCTGACCCGGTTCCAAGCCAGGCTGATTTACCGT
TGCTAACATCCTATCGCACGCATCCCTCTGCCTCATGCACCCAACCCCAAGGCCTGGTACACTGCAGGCC
CCAAGGTCCTGTGCGTCCTTTCAATACCCTCCTCACCTGCCTCACCTGCCCCCCCTACCCTGACTCTGGC
TGGAGACCCCCTCCAGGGAGTTTTCAAAACAAAGGGTGTCAGTCTCCTGTGGGATTCCCTCACCTCTGCA
GCCTGCGGTCTGAAAGCTGCCCCATGGTGTGTAGTGCTAAACTTCCAACTTACTCCAGGCCAGCGGTGAC
AGCCCGAGGGCAGGAAGGGCACCCACACTGAGCCTCAAACAGCTAATTTTGCAACTGTAAGTCCATATAA
TTGTCTTGAAAAGTAATTTGTTTCAAAAAGCTAAAAAACGAATACTCTTGAGTCTCCTTCTAGTAATTCC
CCTTCTAGAGGTCTATCACCAGGAAAAGATCCAAAGCACTGATATTCTTCATGGAGTTGTTTATAATAGA
AAAAAACTAGAGCTTGTTCACAAAGGGGAGCTCTGCAGGCTGAAGATGTTGCACCTGTCAGCGGGGATGG
GGGCACGCTTGCTGACGCAGCAACGGAAAAGCATCAGTGTGTGAAGATGCATTTTCTCTCTTTCTATTAT
TATTATTTTTATTTTTATTTTTTCTGAGGCAGAACC
>known_FOXA1_binding_increase-CtoT_chr16_52597141_52601236
TTAAAAAAAATTACATATATAAACATAAATTTACCACCATAATAGATACTTGTTTTAGTGCAGGTCCTCC
GTGATGCACACTAAGACAGGAATAAACAGGTAAGGGATTTATTGGGGAAAATGCCTGTGAGTGAAAATTG
GAGGTCAAGCACGATGGCTCATGCATATAATCCCAGCACTTTGGGAGGCCGAGGTAGGTAGGCTGCTTGA
GCCCAGGAGTTCAAAATGAGCCTGGGAAACACAGCAAAACCCCATCTCTACAAAAAAACATACAAAAATT
AGCCAGCTATGGTGCCGCATGCCTGTCATCCCACCTACTGGGAGGCTGAGGTGGGAGAATCACTTGAGCC
AGGGAGTTCCAGGCTACAGTGAGTCTTGATCACGCCACTGTGCTCCAGCCTGGGCAACAGACCGAGACCG
TGTCTCAAAAAAATAAATAAATAAAATAAAATAAATGAAAAGAAAAAGTAAAAGAAAATGGTGGGGGAGG
CTGGAGAAGGCCAGGAAAGCCATCAGACTGGGAAGTAGTTCTGTGAAGAGGAGAGAGGAGGATGGATGGT
CTTAGATTCAGTGTCATCCTAAGGAAGTTCTGGCAAGGCCACATTTGCCCATCAGAAGAGTCCCCCTTTC
TCAAGGATGGAACTGGCTTAGTTTTCCTGCTGGGCTTAGCTACAAGCTGGGAACAGCCATTGGGAAGTGT
GGTCTCTGCAGACATGTGGTGATGGATTTGGAGTACAGCAGCTGGGGCTGTCCATCAGTAAGGGATCTAA
GAATTACATGCTCATGGCCACTGCAATGCTAAAATAGAGCAAAACACAATAAAAACAAGATAAATTAGAG
GGACATGATCTGGTCTGGGACAAGGAAGATTGTTTCAGAAAATAGTCTCTGTCTTACATTCACAAGGGAA
AAGAGCAATGTTCCAGAAAGAGTGGCGAGTGCAAAGGCCCTGTGGCAGAAGGCAGCATGGGATGTTCAAG
AAACTGAAAGAAGGCCAGAGCAGCCTAATGGAAATAATATAGGAGAGAGTGGAATGGGAGGCTGAGAGGT
GAGCATTGCTGGGGACCCCTACAAGGCCTTTAAGGAGAGGACATTGTTTTAAACACACATCTAAAACTCA
CTACTTACATGACAGACATAGGGCAGAGGGTGGAGTACTAGGCGTGTGCCTGGGTTTCCAGGTGGCTGAA
CTGTAGCATGACTGGCGGGACTGAAGGAGTGGAGAGGCTAAGGTCTTGATGTCACCTACTTTTGTTAGGA
TTAGAACAGGGGTCACAAACTGTTGGACCCCATGGCAAATGTAATTTTCTTTTTCAATTTTTTATTTTTT
GAGACAGTCTCACTCTGTCACACAGGCTAGAGTGCAGTGGCATGATCTCAGCTCGCGGCAGCCTCAAACT
CCCTGGCTCAAGCCATGCTCCCCCACCTCAGCCTCCTGAGTAGCTGGGACTACAGGCACATGCCACTCCA
CCCGGCTAATTTTTTTTCTTTTTCTTTTTTGTAGAGATGGGGTCTCACCTTGTTGTTCAGGTTGATCTCG
AATTCTTGGGCTCAAGCAGTCCTCCCATCTAGGCTTCCCAAAATGCTGGGATTACAGACATGAGCCACTG
CACCCAGCCACAAAGATAACCTAAAGATGTGTTTACTTTGACCCAGGCAGTAGTTTAAAAAAGTTTTAAT
TTGTTGTTCACATTTAAAAACTGGACAATTTCTACATAAAAATCTGAATTACTCATGTCTCTTAAAAAAA
TAACATCTAGCAATGGTAGGCCCACATTCCTTCCTGAAAATAATTAGCTGGGAAAGAGTAGGGACTGACC
CCTTTAGACACGGTATAAATAGCATGGGAGTTGATCAGTAAATATTTGCTGAATGAAAGAATACATGAAT
GAAAAGTCAGAGCCCTATAGGTCAGCATGGACGGCGGTAAAGGAACCTGGCTGAGCCTGAAAGAGAATGT
GATCTAAGATTAAATCCAGGATATGCTGGTAAATGTTTAACAGCCAACTCTTTGGGGAGGAAAAAAGTCC
CAATTTGTAGTGTTTGCTGATTATTGTGATGTAAATACTCCCATCATGACCAATTTCAAGCTACCAACAT
GCTGACACTGAACTTGGAGTTGGAAGGAGATGAACAGGCATAATCAGGTCTCGTGAGATGGCCCAAGCCG
GCCCCAGCACTCCACTGTTATATATGAGGCTAGAATTACTACATAACTGGAATAGCAACTTTCTGGACCA
TATGCCTGGAACACAGCAGGTGCTGAATAAATGTTTGTTGATCCAGGAACTGACTGTGTTGAAGCCCACA
GATGGGAAATCAGTAGAAGGCAGGTAAGAGTAAAAAGAAGGGCAGAGAATTGGGGGTACAGACCCCTGAA
CCATAAGTCAGAGGAATGTTGTACATGTTTTCAGATCCCTCACTGGTCAAATGAAGGCAAAGGGTTAGAT
CTCTCCAAATCTTTAGAGGGACATGATGTAACTCCATTAAGTAACTCAGTGATTTTCAACATTAAAAAGT
GTAATTATCTTTTCAAACTAAATATTACAAATAAATTCTGATAAAAGGAAATGTACGTCTGTTGAGACCC
CTGATCCTATAACTGGTTTAGTATTCCCTGAAGCCACTTGTAGTAGCCTATTCACATGGGGACTCCCCAG
TGAACATTTCCTCATATCCACACTCTTGTGAAGGTCCTTCACCTTGAATCTGGACTGGGCCTATGGCCTG
ATTTGATGCATAGAATGCACAGATGAGAATAGGTGGGTTTCTGGCATAAGCTCAAAGAAGGCACAGTGGC
TTCCAGAGCCCTGAGCTACTAGTAAGAAAGTCCAGCTACCCTGCTGGGAAGGCCATGGAGAGAGGCCATG
GGGAGCTCATAGAGAAAGAGAGGGACCCTGAGACTACATGGAGCTAGGAAGAACAACACCCAGCCATCCC
AGTGTCCCAGCTGAGCCCCACTCCAAACCCATCTGCCAACTGAGCACATCTACAAGAGTAACCACTGACA
GGCCAGCACAAGCCCAGCCAAGTCCAGTTCAAATTGCAGATTGTAAGCAAATAAAGCAGTTGATGTTTTA
AGGTTTTTAGATGATCTTAGAGTTTAGATTATGTTAAGTATGTGAGATCCCTAAATAGCCATCAATAAAA
AAATTGATCAGTACAATGGAGAACTGCACAACTATTAAAAGAAGAAGGCAAATCCATGTTTACCGACATG
AAAGAAAATCACCAAGATTTACCCTAACATGACAAAGCAAGATTCAGAAGAATCTGTAGGGTATAGTACG
GGATTTGCCATTGCATGAAAAAGGGAAGGTAAGTATTTGTGCATGCGTGCATGTGCATTTGTGTGTGTGT
GTGTGTGTGTGTGACTATAAATTTGTAAATGCATAGAAGATTTCTGGAAAGATACATAAGACATTAGAAC
AGACTGGAAGACTTGAAGAAGAGGGGAGATTGTGTATCCTTTTGTACATATTTTCATTGTGTATCCTTTT
GTACATATTTTTACCTTATTTGCAGGTATCACTTATTCAAATCATAGAAATAACATTTTAACAAAAAAGT
GAGGCCTGCTTATATAATGTTGAAGACAACTAGCTACCACACTATGGGAACACACTATTATATTAAACTT
GATGATTCGAGTTTTGCCTAAATTTTACTCAGAATCGAATACAAGAATAGAGGAAAGAATTTAAAACCCA
TCTGGCTAATGCATACCCTTGCCAACTATTCTATTAGAAAGGAAGATGGCAGTGGGGAGGGGGTTCTTAC
AATGGAAAAGGTATTGGCTTCCCTAGTGCCTGAGGTCGGGTTCTCCAAGAAGCAAATTCAGAGTGAAGGA
TTTTAATGAGAGTAATCTATGTGGGAAGTGGTCCCAAGAAGCACCAAAACAGAAGTGGGGAAAGGACACA
GAGAAGGAAAGGAAGTCACTGAAGGGACTGTGAGTTACCTTTGTGGGCAACTGGGGTTTCGTCCCACTGG
GGACCTCTGGGAGACACTGTAGGACATGTTTCAGGGTGTCCCATCCTAAGTGGGAGGAATCTGGAGAATT
TATACTCTGCTCCCAGGGGCATCAATTCTCCAGTGT`;

export const VCF_EXAMPLE_TEXT_HG19 =
  'chr5\t134871851\tNEUROG1\tA\tG\n' +
  'chr7\t49813589\tVWC2\tG\tC\n' +
  'chr17\t76352731\tSOCS3\tG\tA\n' +
  'chr19\t59070227\tUBE2M\tC\tA\n' +
  'chr8\t74206149\tRDH10\tC\tG\n' +
  'chr11\t69593819\tFGF4_a\tG\tA\n' +
  'chr3\t12598855\tMKRN2\tG\tA\n' +
  'chr11\t2435681\tTRPM5\tC\tT\n' +
  'chr3\t54158012\tCACNA2D3\tT\tC\n' +
  'chr10\t96305280\tHELLS\tT\tC\n' +
  'chr6\t108879283\tFOXO3\tG\tA\n' +
  'chr19\t1854681\tREXO1\tC\tG';

export const VCF_EXAMPLE_TEXT_HG38 =
  'chr5\t135536161\tNEUROG1\tA\tG\n' +
  'chr7\t49773993\tVWC2\tG\tC\n' +
  'chr17\t78356650\tSOCS3\tG\tA\n' +
  'chr19\t58558860\tUBE2M\tC\tA\n' +
  'chr8\t73293914\tRDH10\tC\tG\n' +
  'chr11\t69779051\tFGF4_a\tG\tA\n' +
  'chr3\t12557356\tMKRN2\tG\tA\n' +
  'chr11\t2414451\tTRPM5\tC\tT\n' +
  'chr3\t54123985\tCACNA2D3\tT\tC\n' +
  'chr10\t94545523\tHELLS\tT\tC\n' +
  'chr6\t108558080\tFOXO3\tG\tA\n' +
  'chr19\t1854682\tREXO1\tC\tG';

export const BED_EXAMPLE_TEXT_HG19 =
  'chr1\t109817090\t109817091\t.\t0\t*\n' +
  'chr10\t23507863\t23507864\t.\t0\t*\n' +
  'chr16\t52598688\t52598689\t.\t0\t*\n' +
  'chr16\t209209\t209210\t.\t0\t*\n' +
  'chr16\t83947129\t83947130\t.\t0\t*\n' +
  'chr16\t85833314\t85833315\t.\t0\t*\n' +
  'chr17\t76352731\t76352732\t.\t0\t*\n' +
  'chr19\t1854681\t1854682\t.\t0\t*\n' +
  'chr19\t55999138\t55999139\t.\t0\t*\n' +
  'chr19\t59070227\t59070228\t.\t0\t*\n';

export const BED_EXAMPLE_TEXT_HG38 =
  'chr1\t109274468\t109274469\t.\t0\t*\n' +
  'chr10\t23218934\t23218935\t.\t0\t*\n' +
  'chr16\t52564776\t52564777\t.\t0\t*\n' +
  'chr16\t159210\t159211\t.\t0\t*\n' +
  'chr16\t83913524\t83913525\t.\t0\t*\n' +
  'chr16\t85799708\t85799709\t.\t0\t*\n' +
  'chr17\t78356650\t78356651\t.\t0\t*\n' +
  'chr19\t1854682\t1854683\t.\t0\t*\n' +
  'chr19\t55487771\t55487772\t.\t0\t*\n' +
  'chr19\t58558860\t58558861\t.\t0\t*\n';

export const STRANDED_BED_EXAMPLE_TEXT_HG19 =
  'chr1\t109817090\t109817091\t.\t0\t-\n' +
  'chr10\t23507863\t23507864\t.\t0\t+\n' +
  'chr16\t52598688\t52598689\t.\t0\t-\n' +
  'chr16\t209209\t209210\t.\t0\t-\n' +
  'chr16\t83947129\t83947130\t.\t0\t+\n' +
  'chr16\t85833314\t85833315\t.\t0\t+\n' +
  'chr17\t76352731\t76352732\t.\t0\t+\n' +
  'chr19\t1854681\t1854682\t.\t0\t+\n' +
  'chr19\t55999138\t55999139\t.\t0\t+\n' +
  'chr19\t59070227\t59070228\t.\t0\t+\n';

export const STRANDED_BED_EXAMPLE_TEXT_HG38 =
  'chr1\t109274468\t109274469\t.\t0\t-\n' +
  'chr10\t23218934\t23218935\t.\t0\t+\n' +
  'chr16\t52564776\t52564777\t.\t0\t-\n' +
  'chr16\t159210\t159211\t.\t0\t-\n' +
  'chr16\t83913524\t83913525\t.\t0\t+\n' +
  'chr16\t85799708\t85799709\t.\t0\t+\n' +
  'chr17\t78356650\t78356651\t.\t0\t+\n' +
  'chr19\t1854682\t1854683\t.\t0\t+\n' +
  'chr19\t55487771\t55487772\t.\t0\t+\n' +
  'chr19\t58558860\t58558861\t.\t0\t+\n';

export const STRANDED_VCF_EXAMPLE_TEXT_HG19 =
  'chr1\t109817590\t[known_CEBP_binding_increase]\tG\tT\t-\n' +
  'chr10\t23508363\t[known_FOXA2_binding_decrease]\tA\tG\t+\n' +
  'chr16\t52599188\t[known_FOXA1_binding_increase]\tC\tT\t-\n' +
  'chr16\t209709\t[known_GATA1_binding_increase]\tT\tC\t-\n' +
  'chr16\t83947129\tMLYCD\tT\tA\t+\n' +
  'chr16\t85833314\tCOX4I1\tG\tT\t+\n' +
  'chr17\t76352731\tSOCS3\tG\tA\t+\n' +
  'chr19\t1854681\tREXO1\tC\tG\t+\n' +
  'chr19\t55999138\tSSC5D\tG\tA\t+\n' +
  'chr19\t59070227\tUBE2M\tC\tA\t+\n';

export const STRANDED_VCF_EXAMPLE_TEXT_HG38 =
  'chr1\t109274968\t[known_CEBP_binding_increase]\tG\tT\t-\n' +
  'chr10\t23219434\t[known_FOXA2_binding_decrease]\tA\tG\t+\n' +
  'chr16\t52565276\t[known_FOXA1_binding_increase]\tC\tT\t-\n' +
  'chr16\t159710\t[known_GATA1_binding_increase]\tT\tC\t-\n' +
  'chr16\t83913524\tMLYCD\tT\tA\t+\n' +
  'chr16\t85799708\tCOX4I1\tG\tT\t+\n' +
  'chr17\t78356650\tSOCS3\tG\tA\t+\n' +
  'chr19\t1854682\tREXO1\tC\tG\t+\n' +
  'chr19\t55487771\tSSC5D\tG\tA\t+\n' +
  'chr19\t58558860\tUBE2M\tC\tA\t+\n';

// Analysis characteristics configuration
export interface GenomeExamples {
  regular: string;
  stranded: string;
}

export interface AnalysisCharacteristics {
  sequenceLength: number;
  requiresStranded: boolean;
  fastaExample: string;
  vcfExamples: {
    hg19: GenomeExamples;
    hg38: GenomeExamples;
  };
  bedExamples: {
    hg19: GenomeExamples;
    hg38: GenomeExamples;
  };
}

export const ANALYSIS_CHARACTERISTICS: Record<
  string,
  AnalysisCharacteristics
> = {
  sei: {
    sequenceLength: 4096,
    requiresStranded: false,
    fastaExample: FASTA_EXAMPLE_TEXT_4096BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
  beluga: {
    sequenceLength: 2000,
    requiresStranded: false,
    fastaExample: FASTA_EXAMPLE_TEXT_2000BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
  ism: {
    sequenceLength: 2000,
    requiresStranded: false,
    fastaExample: FASTA_EXAMPLE_TEXT_2000BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
  seqweaver: {
    sequenceLength: 1000,
    requiresStranded: true,
    fastaExample: FASTA_EXAMPLE_TEXT_1000BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
  expecto: {
    sequenceLength: 4096,
    requiresStranded: false,
    fastaExample: FASTA_EXAMPLE_TEXT_4096BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
  expectosc: {
    sequenceLength: 4096,
    requiresStranded: false,
    fastaExample: FASTA_EXAMPLE_TEXT_4096BP,
    vcfExamples: {
      hg19: {
        regular: VCF_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: VCF_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
      },
    },
    bedExamples: {
      hg19: {
        regular: BED_EXAMPLE_TEXT_HG19,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
      },
      hg38: {
        regular: BED_EXAMPLE_TEXT_HG38,
        stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
      },
    },
  },
};

// Default characteristics for unknown analyses
export const DEFAULT_ANALYSIS: AnalysisCharacteristics = {
  sequenceLength: 4096,
  requiresStranded: false,
  fastaExample: FASTA_EXAMPLE_TEXT_4096BP,
  vcfExamples: {
    hg19: {
      regular: VCF_EXAMPLE_TEXT_HG19,
      stranded: STRANDED_VCF_EXAMPLE_TEXT_HG19,
    },
    hg38: {
      regular: VCF_EXAMPLE_TEXT_HG38,
      stranded: STRANDED_VCF_EXAMPLE_TEXT_HG38,
    },
  },
  bedExamples: {
    hg19: {
      regular: BED_EXAMPLE_TEXT_HG19,
      stranded: STRANDED_BED_EXAMPLE_TEXT_HG19,
    },
    hg38: {
      regular: BED_EXAMPLE_TEXT_HG38,
      stranded: STRANDED_BED_EXAMPLE_TEXT_HG38,
    },
  },
};

interface AnalysisMetaInfo {
  name: string;
  fileFormats?: string[];
  textOverview?: string;
  helpUrl: string;
  imageUrl?: string | null;
  defaultModelName?: string;
  genomes?: string[];
}

export const ANALYSES_META_INFO: Record<string, AnalysisMetaInfo> = {
  expecto: {
    name: 'ExPecto',
    fileFormats: ['vcf'],
    textOverview: `ExPecto leverages deep learning-based sequence models trained on chromatin profiling data,
     and integrated with spatial transformation and regularized linear models. This framework does not use any
     variant information, enabling prediction of the expression effect for any variant, even those that are rare
     or have never been previously observed.`,
    helpUrl: `${DOC_URL}expecto.html`,
    imageUrl: null,
    defaultModelName: 'beluga',
    genomes: ['hg19'],
  },
  deepsea: {
    name: 'DeepSEA',
    fileFormats: ['vcf', 'bed', 'fasta'],
    textOverview: `DeepSEA is a deep learning framework that predicts genomic variant effects on a wide range
     of regulatory features with single nucleotide sensitivity: transcription factors binding, DNase I hypersensitive
     sites, and histone marks in multiple human cell types.`,
    helpUrl: `${DOC_URL}deepsea.html`,
    imageUrl: `/deepsea/deepsea_logo.svg`,
    defaultModelName: 'sei',
    genomes: ['hg19', 'hg38'],
  },
  sei: {
    name: 'Sei',
    fileFormats: ['vcf', 'bed', 'fasta'],
    textOverview: `Sei is a deep-learning-based framework for systematically predicting sequence regulatory activities and applying sequence information to understand human genetics data.`,
    helpUrl: `${DOC_URL}sei.html`,
    defaultModelName: 'sei',
    genomes: ['hg19', 'hg38'],
  },
  beluga: {
    name: 'Beluga',
    fileFormats: ['vcf', 'bed', 'fasta'],
    textOverview: `Beluga is a deep learning framework for predicting genomic variant effects on regulatory features using a convolutional neural network trained on chromatin profiling data.`,
    helpUrl: `${DOC_URL}beluga.html`,
    defaultModelName: 'beluga',
    genomes: ['hg19', 'hg38'],
  },
  seqweaver: {
    name: 'Seqweaver',
    fileFormats: ['vcf', 'bed', 'fasta'],
    textOverview: `Seqweaver is a deep learning framework for predicting RNA-binding protein target site dysregulation and RNA sequence variant effects.`,
    helpUrl: `${DOC_URL}seqweaver.html`,
    defaultModelName: 'seqweaver',
    genomes: ['hg19', 'hg38'],
  },
  ism: {
    name: 'In Silico Mutagenesis',
    fileFormats: ['fasta'],
    textOverview: `Perform "In silico saturated mutagenesis" analysis to discover informative regulatory features within any sequence. Specifically, it performs computational mutation scanning by accessing the predicted regulatory effect of mutating every base of the input sequence.`,
    helpUrl: `${DOC_URL}in-silico-mutagenesis.html`,
    defaultModelName: 'beluga',
    genomes: ['hg19', 'hg38'],
  },
  expectosc: {
    name: 'ExPectoSC',
    fileFormats: ['vcf'],
    textOverview: `ExPectoSC extends ExPecto's capabilities to predict cell-type-specific chromatin effects and expression changes at single-cell resolution using deep learning models trained on chromatin profiling data.`,
    helpUrl: `${DOC_URL}expectosc.html`,
    defaultModelName: 'expectosc',
    genomes: ['hg19', 'hg38'],
  },
  gene_networks: {
    name: 'Functional gene networks',
    helpUrl: `${DOC_URL}functional-networks.html`,
  },
  modules: {
    name: 'Functional module detection',
    helpUrl: `${DOC_URL}modules.html`,
  },
  seek: {
    name: 'SEEK',
    helpUrl: 'https://humanbase.readthedocs.io/en/seek/seek.html',
  },
  netwas: {
    name: 'NetWAS',
    helpUrl: `${DOC_URL}netwas.html`,
  },
  tissue_networks: {
    name: 'Tissue Networks',
    helpUrl: `${DOC_URL}tissue-networks.html`,
  },
  asdbrowser: {
    name: 'ASD Browser',
    helpUrl: `${DOC_URL}asdbrowser.html`,
  },
};

export const EXPECTO_EXAMPLE_GENES: ExampleGene[] = [
  {
    description: 'Search for ExPecto variant effect predictions for IRGM',
    buttonText: 'IRGM',
    pasteText: 'IRGM',
    searchUrl: `${SEARCH_HUMAN_EXACT_URI}irgm`,
    url: '/gene/345611',
  },
  {
    description: 'Search for ExPecto variant effect predictions for CCR1',
    buttonText: 'CCR1',
    pasteText: 'CCR1',
    searchUrl: `${SEARCH_HUMAN_EXACT_URI}ccr1`,
    url: '/gene/1230',
  },
];

interface ExampleRsidFilter {
  description: string;
  buttonText: string;
  pasteText: string;
  searchUrl: string;
}

export const EXPECTO_EXAMPLE_RSID_FILTER: ExampleRsidFilter[] = [
  {
    description: 'Filter for enriched rsIDs related to IRGM',
    buttonText: 'IRGM',
    pasteText: 'IRGM',
    searchUrl: `rsid__isempty=False&page=1&gene__standard_name__istartswith=IRGM`,
  },
  {
    description: 'Find ExPecto predictions by rsID',
    buttonText: 'rs531708574',
    pasteText: 'rs531708574',
    searchUrl: `rsid__isempty=False&page=1&rsid__startswith=rs531708574`,
  },
  {
    description: 'Search for rsIDs with ExPecto predictions by genome position',
    buttonText: 'chr1:877047',
    pasteText: 'chr1:877047',
    searchUrl: `rsid__isempty=False&page=1&chromosome=chr1&position__startswith=877047`,
  },
];

interface ExampleVariant {
  description: string;
  buttonText: string;
  pasteText: string;
}

export const EXPECTO_EXAMPLE_VARIANTS: ExampleVariant[] = [
  {
    description: 'Search for rsIDs with ExPecto predictions for IRGM',
    buttonText: 'variants',
    pasteText: STRANDED_VCF_EXAMPLE_TEXT_HG19,
  },
];

interface ExpectoConfig {
  name: string;
  database: string;
  overviewLabel: string;
  tissueLabel: string;
  range: [number, number, number];
}

export const EXPECTO_CONFIG: ExpectoConfig = {
  name: 'ExPecto',
  database: 'expecto-tissues',
  overviewLabel: 'Tissue System',
  tissueLabel: 'Tissue',
  range: [-1.0, -0, 1.0],
};

export const EXPECTOSC_EXAMPLE_GENES: ExampleGene[] = [
  {
    description: 'Search for ExPectoSC variant effect predictions for PTEN',
    buttonText: 'PTEN',
    pasteText: 'PTEN',
    searchUrl: `${BASE_URI}genes/search/exact/?q=pten`,
    url: '/gene/5728',
  },
];

export const EXPECTOSC_CONFIG: ExpectoConfig = {
  name: 'ExPectoSC',
  database: 'clever-tissues',
  overviewLabel: 'Organ',
  tissueLabel: 'Cell-type',
  range: [-100.0, 0, 100.0],
};

export const DEEPSEA_ANALYSES = {
  deepsea: 'deepsea',
  expecto: 'expecto',
  sei: 'sei',
  beluga: 'beluga',
  seqweaver: 'seqweaver',
  ism: 'ism',
};

export const SEEK_EXAMPLE_GENES: ExampleSearch[] = [
  {
    description:
      'Search Hedgehog (Hh) pathway using transcription factors and receptor protein',
    buttonText: 'GLI1 GLI2 PTCH1',
    pasteText: 'GLI1 GLI2 PTCH1',
  },
];

export const MISSING_POSITION_INTERVAL = 50000;
export const INTERVAL_DISTANCE = 50000;

// Environment variables with type assertions
export const STATIC_HOST_URL = process.env.STATIC_HOST_URL as string;

export const NETWORK_MAX_THRESHOLD = 30;
export const COMMUNITY_MIN_THRESHOLD = 20;
export const MAX_GENE_QUERY_SIZE = 3500;
export const MAX_RECOMMENDED_GENE_QUERY_SIZE = 2550;

interface DeepSeaModel {
  name: string;
  label: string;
  vizInputTypes: string[];
  stranded: boolean;
}

export const DEEPSEA_MODELS: DeepSeaModel[] = [
  {
    name: 'Sei',
    label: 'Sei (2021 DNA model)',
    vizInputTypes: ['vcf'],
    stranded: false,
  },
  {
    name: 'Beluga',
    label: 'Beluga (2019 DNA model)',
    vizInputTypes: ['vcf', 'bed'],
    stranded: false,
  },
  {
    name: 'Seqweaver',
    label: 'Seqweaver (RNA model)',
    vizInputTypes: ['vcf', 'bed'],
    stranded: true,
  },
];

export const IS_LOCAL_XSTATE_VIZ = 'IS_LOCAL_XSTATE_VIZ';
export const DEBUG_XSTATE_FMD = 'DEBUG_XSTATE_FMD';
export const DEBUG_XSTATE_GENE_SEARCH_RESULT_TABLE =
  'DEBUG_XSTATE_GENE_SEARCH_RESULT_TABLE';
export const DEBUG_XSTATE_PASTEBOX = 'DEBUG_XSTATE_PASTEBOX';
export const DEBUG_XSTATE_SEEK_DATASET_SELECT_FILTER =
  'DEBUG_XSTATE_SEEK_DATASET_SELECT_FILTER';
export const DEBUG_XSTATE_SEEK_PAGE = 'DEBUG_XSTATE_SEEK_PAGE';
export const DEBUG_XSTATE_TERM_SEARCHABLE_MULTISELECT =
  'DEBUG_XSTATE_TERM_SEARCHABLE_MULTISELECT';
export const DEBUG_XSTATE_WHATS_NEW = 'DEBUG_XSTATE_WHATS_NEW';
export const XSTATE_DEBUG_COOKIES = [
  DEBUG_XSTATE_FMD,
  DEBUG_XSTATE_GENE_SEARCH_RESULT_TABLE,
  DEBUG_XSTATE_PASTEBOX,
  DEBUG_XSTATE_SEEK_PAGE,
  DEBUG_XSTATE_SEEK_DATASET_SELECT_FILTER,
  DEBUG_XSTATE_TERM_SEARCHABLE_MULTISELECT,
  DEBUG_XSTATE_WHATS_NEW,
];

export const DEEPSEA_COOKIE_NAME = 'savedDeepSEAJobs';
export const GENOME_ASSEMBLY_COOKIE = 'genomeAssembly';
export const DEEPSEA_JOB_RETENTION_DAYS = 365; // 1 year

interface CookieOptions {
  path: string;
  maxAge: number;
}

export const COOKIE_OPTIONS: CookieOptions = {
  path: '/',
  maxAge: 60 * 60 * 24 * 365, // 1 year in seconds
};

// Update the version in the constant HIDE_LATEST_BANNER_COOKIE to
//  re-enable both the banner and notification dot for the next deployment.
export const HIDE_LATEST_BANNER_COOKIE = 'whatsNewBannerCookie-2026-02-06';
export const HIDE_LATEST_NOTIFICATION_COOKIE = `${HIDE_LATEST_BANNER_COOKIE}-notification`;

const CHROMOSOMES = ['chrX', 'chrY'];
for (let i = 1; i <= 22; i += 1) {
  CHROMOSOMES.push(`chr${i.toString()}`);
}

export { CHROMOSOMES };

export const SEQ_CAT_COLORS: Record<string, string> = {
  E: '#984ef3',
  CTCF: '#fdb462',
  PC: '#8abad4',
  'P ': '#ef3b2c',
  HET: '#662506',
  TN: '#fb9a99',
  TF: '#386cb0',
  L: '#C0C0C0',
};

export const SEQ_CAT_NAMES: Record<string, string> = {
  E: 'Enhancer',
  CTCF: 'CTCF',
  PC: 'Polycomb',
  'P ': 'Promoter',
  HET: 'Heterochomatin',
  TN: 'Transcription',
  TF: 'Transcription factor',
  L: 'Low signal',
};

export const AWS_URL = 'https://humanbase.s3.amazonaws.com';

export const MAX_COOKIE_AGE = 60 * 60 * 24 * 365; // a year

export const SEEK_URI = `${BASE_URI}seek/`;
export const SEEK_UUID_SEED = '91cf34a0-32d1-46c1-9f05-fb1e6923fa8c';

// Update this when the browser caches need invalidating
export const HB_API_VERSION = 'v2025-02-11';

// Network versions (GIANT v1, MAGE v3)
export const MAGE_WAS_SUBMISSIONS_ENABLED = false;

export const NETWORK_VERSIONS = {
  GIANT: 'v1',
  MAGE: 'v3',
} as const;

export const ALL_NETWORK_VERSIONS = Object.values(NETWORK_VERSIONS);

// Reverse lookup: version value -> display name (e.g., 'v3' -> 'MAGE')
export const NETWORK_VERSION_NAMES: Record<string, string> = {};
Object.entries(NETWORK_VERSIONS).forEach(([k, v]: [string, string]) => {
  NETWORK_VERSION_NAMES[v] = k;
});

// Version identifiers used in API calls and database queries
export const VERSION_IDENTIFIERS = {
  [NETWORK_VERSIONS.GIANT]: '1',
  [NETWORK_VERSIONS.MAGE]: '3',
} as const;
